import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import {
  Box,
  Grid,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Typography,
  Button,
  Card,
  CardContent,
  Skeleton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import {
  DescriptionOutlined as DocumentIcon,
  BorderColorOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import CollapsibleSection, {
  calculateCoApplicantCompletionSummary,
} from "../common-features/CollapsibleSection";
import HorizontalLinearStepper from "../common-features/HorizontalLinearStepper";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserApplicationsFormData } from "../../services/api/service/userApplicationService";
import applicationDetailsServices from "../../services/api/service/applicationDetails";
import { AppDispatch, RootState } from "../../services/redux/store";
import {
  setActiveHorizontalIndex,
  setActiveVerticalIndex,
  requestSummaryFormSave,
  setSummaryFormSaveInProgress,
  setCoApplicantSummaryViewOnly,
  setDisableAddMoreCoApplicant,
  setFetchUserApplicationData,
  setSummaryApplicationEditActive,
  setSummaryEditOpenedFromCoApplicantSection,
  setSummaryReferenceCollateralEditTab,
  setLoanDetailCoApplicantCollapseValue,
  LOAN_DETAILS_SUMMARY_EDIT_FOCUS_KEY,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import DetailRow from "../common-features/DetailRow";
import ModalPopup from "../../components/ui/ModalPopup";
import { authSelector } from "../../services/redux/slices/authSlice"; 
import successProvisionalOffer from "./../../assets/images/success_provisional_offer.svg";
import failedProvisionalOffer from "./../../assets/images/failed_provisional_offer.png";
import pendingProvisionalOffer from "./../../assets/svgIcons/pending_provisional_offer.svg";
import eligibilityCheckIcon from "./../../assets/svgIcons/clipboardCheckAppDetails.svg";
import applicantAndLoanDetailsIcon from "./../../assets/svgIcons/list-detailsAppDetails.svg";
import provisionalOfferLetterIcon from "./../../assets/svgIcons/mailOpenedAppDetails.svg";
import addtionalDetailsAndDocumentsIcon from "./../../assets/svgIcons/idAppDetails.svg";
import finalVerificationAndSanctionIcon from "./../../assets/svgIcons/file-textAppDetails.svg";
import coApplicantIcon from "./../../assets/svgIcons/users.svg";
import DisbursementOfFundsIcon from "./../../assets/icons/file-check.png";
import leftConfetti from "./../../assets/svgIcons/left-confetti.svg";
import rightConfetti from "./../../assets/svgIcons/right-confetti.svg";
import {
  setApplicantLoanDetails,
  setAcademicDetails,
  setEntranceDetails,
  setLoanDetails,
  setEmploymentDetails,
  setExpenseDetails,
  setReferenceDetails,
  setCoApplicantDetails,
  setCoApplicantCompletionRate,
  setCollateralDetails,
  setEducationGap,
} from "../../services/redux/slices/applicationLoanDetailsSlice";
import EntranceExamDetailsTab from "../../components/applicantDetailsTabs/EntranceExamDetailsTab";
import LoanDetailsTab from "../../components/applicantDetailsTabs/LoanDetailsTab";
import StudentDetailsTab from "../../components/applicantDetailsTabs/StudentDetailsTab";
import AddressDetailsTab from "../../components/applicantDetailsTabs/AddressDetailsTab";
import EmploymentDetailsTab from "../../components/applicantDetailsTabs/EmploymentDetailsTab";
import AcademicDetailsTab from "../../components/applicantDetailsTabs/AcademicDetailsTab";
import ExpenseDetailsTab from "../../components/applicantDetailsTabs/ExpenseDetailsTab";
import ReferenceDetailsTab, {
  referenceDetailsSkeleton,
} from "../../components/applicantDetailsTabs/ReferenceDetailsTab";
import CoApplicantTabs from "../../components/applicantDetailsTabs/CoApplicantTabs";
import { isAdditionalDetailsSectionEditBlocked } from "../../utils/additionalDetailsValidation";
import BottomSheet from "../../components/ui/BottomSheet";
import CollateralDetailsTab from "../../components/applicantDetailsTabs/CollateralDetailsTab";
import ProvideConsentDetailsTab from "../../components/applicantDetailsTabs/ProvideConsentDetailsTab";
import ProvideCourseDetailsTab from "../../components/applicantDetailsTabs/ProvideCourseDetailsTab";
import ProvideLoanDetailsTab from "../../components/applicantDetailsTabs/ProvideLoanDetailsTab";
import { getFormJson } from "../../services/redux/thunks/stepperDynamicFormThunks";
import SummarySectionHeader from "../../components/applicantDetailsTabs/SummarySectionHeader";
import { summaryActionButtonStyles } from "../../components/ui/commonCss";

// --- Helper Functions and Interfaces ---
function a11yProps(index: number) {
  return {
    id: `tab-${index}`,
    "aria-controls": `tabpanel-${index}`,
  };
}

const toTitleLabel = (key: string): string => {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/\s+/g, " ")
    .replace(/^./, (str) => str.toUpperCase())
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .trim();
};

interface Field {
  [key: string]: any;
}
interface VerticalStep {
  verticalStepName: string;
  fields: Field[];
}

const REFERENCE_AND_COLLATERAL_STEP_NAME = "Reference & Collateral Details";
const REFERENCE_DETAILS_STEP_NAME = "Reference Details";
const COLLATERAL_DETAILS_STEP_NAME = "Collateral Details";
const FUNDS_AND_EXPENSES_STEP_NAME = "Funds & Expenses";

const DEFAULT_ADDITIONAL_DETAILS_TABS: VerticalStep[] = [
  { verticalStepName: "Applicant Additional Details", fields: [] },
  { verticalStepName: FUNDS_AND_EXPENSES_STEP_NAME, fields: [] },
  { verticalStepName: REFERENCE_DETAILS_STEP_NAME, fields: [] },
  { verticalStepName: COLLATERAL_DETAILS_STEP_NAME, fields: [] },
];


const normalizeSummarySectionLabel = (label: string | undefined): string => {
  const value = String(label || "").trim();
  return value;
};

const normalizeSummaryVerticalStepName = (name: string | undefined): string => {
  const value = String(name || "").trim();
  if (value === "Funds and Expenses") {
    return FUNDS_AND_EXPENSES_STEP_NAME;
  }
  return value;
};

const isHiddenApplicantLoanVerticalStep = (name: string | undefined) => {
  const value = String(name || "").trim();
  return value === "Loan Details" || value === "Co-Applicant Details";
};

const isApplicantLoanCoApplicantVerticalStep = (verticalStep: any) => {
  const value = String(verticalStep?.verticalStepName || "").trim();
  return (
    Number(verticalStep?.verticalStepId) === 9 ||
    value === "Loan Details" ||
    value === "Co-Applicant Details"
  );
};

const isHiddenAdditionalDetailsVerticalStep = (name: string | undefined) => {
  const value = String(name || "").trim();
  return (
    value === "Co-Applicant Aditional Details" ||
    value === "Co-Applicant Additional Details"
  );
};

const getSummaryFieldName = (field: any): string => {
  if (!field || typeof field !== "object") return "";
  if (field?.name) return String(field?.name);
  if (field?.formFieldKey) return String(field?.formFieldKey);
  return (
    Object.keys(field).find(
      (key) =>
        ![
          "id",
          "label",
          "type",
          "required",
          "options",
          "fieldOrder",
          "formFieldId",
          "formFieldValue",
          "isRemoved",
        ].includes(key)
    ) || ""
  );
};

const isReferenceSummaryField = (field: any) => {
  const name = getSummaryFieldName(field).toLowerCase();
  return name.startsWith("basicreference") || name.startsWith("addressreference");
};

const isCollateralSummaryField = (field: any) => {
  const name = getSummaryFieldName(field).toLowerCase();
  return name.startsWith("collateral") || name.startsWith("coownercollateral");
};

/** Merges inner vertical steps; normalises legacy / alternate labels. */
function mergeAdditionalDetailsTabs(sourceTabs: VerticalStep[]): VerticalStep[] {
  const normalized = new Map<string, VerticalStep>();

  const appendStep = (key: string, tab: VerticalStep, fields: any[]) => {
    const prev = normalized.get(key);
    if (prev) {
      normalized.set(key, {
        ...prev,
        fields: [...(prev.fields || []), ...(fields || [])],
      });
    } else {
      normalized.set(key, { ...tab, verticalStepName: key, fields });
    }
  };

  (sourceTabs || []).forEach((tab) => {
    const name = tab?.verticalStepName;
    if (!name) return;
    if (
      name === "Co-Applicant Aditional Details" ||
      name === "Co-Applicant Additional Details"
    ) {
      return;
    }
    let key = name;
    if (name === "Funds and Expenses") {
      key = FUNDS_AND_EXPENSES_STEP_NAME;
    }

    if (name === REFERENCE_AND_COLLATERAL_STEP_NAME) {
      appendStep(
        REFERENCE_DETAILS_STEP_NAME,
        tab,
        (tab.fields || []).filter(isReferenceSummaryField)
      );
      appendStep(
        COLLATERAL_DETAILS_STEP_NAME,
        tab,
        (tab.fields || []).filter(isCollateralSummaryField)
      );
      return;
    }

    if (name === REFERENCE_DETAILS_STEP_NAME || name === COLLATERAL_DETAILS_STEP_NAME) {
      appendStep(
        REFERENCE_DETAILS_STEP_NAME,
        tab,
        (tab.fields || []).filter(isReferenceSummaryField)
      );
      appendStep(
        COLLATERAL_DETAILS_STEP_NAME,
        tab,
        (tab.fields || []).filter(isCollateralSummaryField)
      );
      return;
    }

    appendStep(key, tab, tab.fields || []);
  });

  return DEFAULT_ADDITIONAL_DETAILS_TABS.map(
    (def) => normalized.get(def.verticalStepName) ?? def
  );
}

/** Pull summary tab payloads from fetch-application (`getApplicationFormData`) response. */
function extractAdditionalDetailsFromApplication(application: any): {
  expenseDetails?: any[];
  referenceDetails?: any[];
  collateralDetails?: any[];
} {
  if (!application || typeof application !== "object") {
    return {};
  }
  const a = application;
  const nested =
    a.additionalDetailsAndDocuments ??
    a.additionalDetails ??
    a.applicationAdditionalDetails ??
    {};

  const arr = (v: unknown): any[] | undefined =>
    Array.isArray(v) ? (v as any[]) : undefined;

  const expenseDetails =
    arr(a.expenseDetails) ??
    arr(a.ExpenseDetails) ??
    arr(nested.expenseDetails) ??
    arr(nested.ExpenseDetails);

  const referenceDetails =
    arr(a.referenceDetails) ??
    arr(a.ReferenceDetails) ??
    arr(nested.referenceDetails) ??
    arr(nested.ReferenceDetails);

  const collateralDetails =
    arr(a.collateralDetails) ??
    arr(a.CollateralDetails) ??
    arr(a.applicationCollateralDetails) ??
    arr(a?.loanApplicationDetails?.collateralDetails) ??
    arr(nested.collateralDetails) ??
    arr(nested.CollateralDetails);

  return { expenseDetails, referenceDetails, collateralDetails };
}

function mergeVerticalStepFields(
  fields: Field[] | undefined
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  (fields || []).forEach((blob) => {
    if (blob && typeof blob === "object") {
      Object.entries(blob).forEach(([k, v]) => {
        out[k] = v;
      });
    }
  });
  return out;
}

function formatFlatFieldValue(v: unknown): string | number {
  if (v === null || v === undefined) return "";
  if (typeof v === "object" && v !== null) {
    const o = v as { label?: unknown; value?: unknown };
    if ("label" in o || "value" in o) {
      const lab = o.label != null ? String(o.label) : "";
      const val = o.value != null ? String(o.value) : "";
      return lab || val;
    }
  }
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return v as string | number;
}

/** Maps fetch-application `Funds & Expenses` field blobs to ExpenseDetailsTab rows. */
function fieldsToExpenseDetailsRows(fields: Field[] | undefined): any[] {
  const m = mergeVerticalStepFields(fields);
  const expenseDefs: { api: string; key: string; label: string }[] = [
    { api: "expenseTuitionFees", key: "tuitionFees", label: "Tuition Fees" },
    {
      api: "expenseLivingExpenses",
      key: "livingExpenses",
      label: "Living Expenses",
    },
    {
      api: "expenseTravelExpenses",
      key: "travelExpenses",
      label: "Travel Expenses",
    },
    { api: "expenseOtherExpenses", key: "otherExpenses", label: "Other Expenses" },
    { api: "expenseVasExpense", key: "vasExpense", label: "VAS Expense" },
  ];
  const fundDefs: { api: string; key: string; label: string }[] = [
    {
      api: "fundRequestedLoanAmount",
      key: "loanRequestedAmt",
      label: "Loan Requested Amount",
    },
    { api: "fundOwnSources", key: "ownSourceAmt", label: "Own Sources" },
    {
      api: "fundScholarshipAmount",
      key: "scholarshipAmt",
      label: "Scholarship Amount",
    },
    { api: "fundOtherIncome", key: "otherIncomeAmt", label: "Other Income" },
  ];

  const hasValue = (api: string) => {
    const raw = m[api];
    return raw !== undefined && raw !== null && String(raw).trim() !== "";
  };
  if (!expenseDefs.some((d) => hasValue(d.api)) && !fundDefs.some((d) => hasValue(d.api))) {
    return [];
  }

  const rows: any[] = [];
  let totalExpense = 0;
  for (const { api, key, label } of expenseDefs) {
    if (!hasValue(api)) continue;
    const raw = m[api];
    const num = Number(String(raw).replace(/,/g, ""));
    if (!Number.isNaN(num)) totalExpense += num;
    rows.push({ key, label, value: raw });
  }
  rows.push({
    key: "totalExpenseAmt",
    label: "Total Expense",
    value: String(totalExpense),
  });

  let totalFunds = 0;
  for (const { api, key, label } of fundDefs) {
    if (!hasValue(api)) continue;
    const raw = m[api];
    const num = Number(String(raw).replace(/,/g, ""));
    if (!Number.isNaN(num)) totalFunds += num;
    rows.push({ key, label, value: raw });
  }
  rows.push({
    key: "totalFundsAmt",
    label: "Total Funds",
    value: String(totalFunds),
  });
  return rows;
}

/** Builds ReferenceDetailsTab payload from flat Reference & Collateral fields. */
function fieldsToReferenceDetailsFromFlat(
  flat: Record<string, unknown>
): any[] {
  const indices = new Set<number>();
  Object.keys(flat).forEach((k) => {
    const b = k.match(/^basicReference(\d+)/i);
    const a = k.match(/^addressReference(\d+)/i);
    if (b) indices.add(parseInt(b[1], 10));
    if (a) indices.add(parseInt(a[1], 10));
  });
  if (indices.size === 0) return [];
  return Array.from(indices)
    .sort((x, y) => x - y)
    .map((idx) => {
      const entry: { label: string; name: string; value: string | number }[] =
        [];
      Object.keys(flat).forEach((k) => {
        const basicMatch = k.match(new RegExp(`^basicReference${idx}(.+)$`, "i"));
        const addressMatch = k.match(new RegExp(`^addressReference${idx}(.+)$`, "i"));
        const labelKey = basicMatch?.[1] || addressMatch?.[1];
        if (labelKey) {
          entry.push({
            label: toTitleLabel(labelKey),
            name: k,
            value: formatFlatFieldValue(flat[k]),
          });
        }
      });
      return { [`reference${idx}`]: entry };
    });
}

/** Builds CollateralDetailsTab payload from flat Reference & Collateral fields. */
function fieldsToCollateralDetailsFromFlat(
  flat: Record<string, unknown>
): any[] {
  const hasCollateralValue = String(flat?.hasCollateral ?? "").trim();
  if (hasCollateralValue.toLowerCase() === "no") {
    return [
      {
        collateral1: {
          collateral: [
            {
              name: "hasCollateral",
              label: "Has Collateral",
              value: "No",
            },
          ],
          ownership: [],
        },
      },
    ];
  }

  const indices = new Set<number>();
  Object.keys(flat).forEach((k) => {
    if (/^coOwnerCollateral\d+/i.test(k)) {
      const m = k.match(/^coOwnerCollateral(\d+)/i);
      if (m) indices.add(parseInt(m[1], 10));
    } else {
      const c = k.match(/^collateral(\d+)/i);
      if (c) indices.add(parseInt(c[1], 10));
    }
  });
  if (indices.size === 0) return [];

  return Array.from(indices)
    .sort((x, y) => x - y)
    .map((n) => {
      const collateral: { name: string; label: string; value: string | number }[] =
        [];
      const ownershipGroups = new Map<
        number,
        { name: string; label: string; value: string | number }[]
      >();

      Object.keys(flat).forEach((k) => {
        const mo = k.match(
          new RegExp(`^coOwnerCollateral${n}(?:_(\\d+))?(.+)$`, "i")
        );
        if (mo) {
          const ownerIndex = Number(mo[1] || 1);
          const ownerFields = ownershipGroups.get(ownerIndex) || [];
          ownerFields.push({
            name: k,
            label: toTitleLabel(mo[2]),
            value: formatFlatFieldValue(flat[k]),
          });
          ownershipGroups.set(ownerIndex, ownerFields);
          return;
        }
        const mc = k.match(new RegExp(`^collateral${n}(.+)$`, "i"));
        if (mc) {
          collateral.push({
            name: k,
            label: toTitleLabel(mc[1]),
            value: formatFlatFieldValue(flat[k]),
          });
        }
      });

      const ownership = Array.from(ownershipGroups.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([ownerIndex, ownerFields]) => ({
          [`coOwner${n}_${ownerIndex}`]: ownerFields,
        }));

      if (!collateral.length && !ownership.length) {
        return null;
      }
      return {
        [`collateral${n}`]: { collateral, ownership },
      };
    })
    .filter(Boolean) as any[];
}

/** Hydrates Redux for summary tabs from `horizontalSteps` → Additional Details vertical steps. */
function hydrateAdditionalDetailsReduxFromHorizontalSteps(
  application: any,
  dispatch: AppDispatch
): void {
  const addl = (application?.horizontalSteps || []).find(
    (h: any) => h?.horizontalStepName === "Additional Details & Documents"
  );
  const vs = addl?.verticalSteps;
  if (!Array.isArray(vs)) return;

  let referenceRows: any[] = [];
  let collateralRows: any[] = [];

  for (const v of vs) {
    const name = v?.verticalStepName;
    if (name === FUNDS_AND_EXPENSES_STEP_NAME || name === "Funds and Expenses") {
      const rows = fieldsToExpenseDetailsRows(v?.fields);
      if (rows.length > 0) {
        dispatch(setExpenseDetails(rows));
      }
    }
    if (name === REFERENCE_AND_COLLATERAL_STEP_NAME) {
      const flat = mergeVerticalStepFields(v?.fields);
      const refs = fieldsToReferenceDetailsFromFlat(flat);
      const cols = fieldsToCollateralDetailsFromFlat(flat);
      if (refs.length > 0) {
        referenceRows = [...referenceRows, ...refs];
      }
      if (cols.length > 0) {
        collateralRows = [...collateralRows, ...cols];
      }
    }
    if (name === REFERENCE_DETAILS_STEP_NAME || name === COLLATERAL_DETAILS_STEP_NAME) {
      const flat = mergeVerticalStepFields(v?.fields);
      const refs = fieldsToReferenceDetailsFromFlat(flat);
      const cols = fieldsToCollateralDetailsFromFlat(flat);
      if (refs.length > 0) {
        referenceRows = [...referenceRows, ...refs];
      }
      if (cols.length > 0) {
        collateralRows = [...collateralRows, ...cols];
      }
    }
  }

  if (referenceRows.length > 0) {
    dispatch(setReferenceDetails(referenceRows));
  }
  if (collateralRows.length > 0) {
    dispatch(setCollateralDetails(collateralRows));
  }
}

const getPartnerDetailsArray = (data: any, keys: string[] = []) => {
  if (Array.isArray(data)) {
    const firstValue =
      data.length === 1 && data[0] && typeof data[0] === "object"
        ? Object.values(data[0]).find(Array.isArray)
        : null;
    return Array.isArray(firstValue) ? firstValue : data;
  }
  if (data && typeof data === "object") {
    const keyedValue = keys.map((key) => data[key]).find(Array.isArray);
    if (Array.isArray(keyedValue)) return keyedValue;
    const firstArray = Object.values(data).find(Array.isArray);
    return Array.isArray(firstArray) ? firstArray : [];
  }
  return [];
};

interface HorizontalStep {
  horizontalStepName: string;
  verticalSteps: VerticalStep[];
  completionRate?: number;
}
interface Application {
  horizontalSteps: HorizontalStep[];
  submissionId: string;
  isProvisionalGenerated?: boolean;
  isQualified?: boolean;
  isEligibilityChecked?: boolean | null;
}
interface CombinedStep {
  label: string;
  completionRate: string;
  status: "pending" | "completed";
  innerData: VerticalStep[];
  isEligible?: boolean | null;
}
type RawDataItem = {
  [key: string]: string | number | boolean | undefined;
};
type FormattedItem = {
  key?: string;
  label: string;
  value: string | number | boolean | undefined;
  isVerified: boolean;
  showNotVerified?: boolean;
};
interface DetailRowComponentProps {
  item: FormattedItem;
}
type DetailRowType = React.FC<DetailRowComponentProps>;
interface InnerDetailsTabsProps {
  innerData: VerticalStep[];
  activeTabIndex: number;
  activeHandleChange: (event: React.SyntheticEvent, newValue: number) => void;
  isXsOrSm: boolean;
}
interface ApplicationDetailsTabsProps {
  application: CombinedStep;
  tabIndex: number;
  handleChange: (event: React.SyntheticEvent, newValue: number) => void;
  title: string;
  completionRate: string;
  onClose: () => void;
  isLoanDetailsLoading: boolean;
  isXsOrSm: boolean;
  coApplicantDetails: any;
  coApplicantCompletionRate: any;
  designationsVal:any;
}

const TabPanel: React.FC<{
  children?: React.ReactNode;
  index: number;
  value: number;
}> = ({ children, value, index, ...other }) => {
  const stepName =
    React.isValidElement(children) &&
    (children.props as { verticalStep?: VerticalStep })?.verticalStep
      ?.verticalStepName;
  const isReferenceOrCollateralPanel =
    stepName === "Reference Details" ||
    stepName === "Collateral Details" ||
    stepName === REFERENCE_AND_COLLATERAL_STEP_NAME;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: isReferenceOrCollateralPanel ? 0 : 2, pt: 0 }}>{children}</Box>
      )}
    </div>
  );
};

export const tabContentSkeleton = () =>
  Array.from({ length: 8 })?.map((_, idx) => (
    <Grid
      item
      xs={12}
      sm={6}
      md={3}
      sx={{ mb: 2 }}
      key={`student-skeleton-${idx}`}
    >
      <Skeleton
        variant="rectangular"
        width="100%"
        height={15}
        sx={{ borderRadius: "4px", mb: 1 }}
      />
      <Skeleton
        variant="rectangular"
        width="100%"
        height={15}
        sx={{ borderRadius: "8px" }}
      />
    </Grid>
  ));

/** API often sends "true"/"1"/true; never treat "false" or other strings as verified. */
const isStrictVerifiedFlag = (v: unknown): boolean =>
  v === true || v === "true" || v === 1 || v === "1";

const PARTNER_APPLICANT_ADDITIONAL_STUDENT_KEYS = [
  "applicantdependents",
  "applicantmaritalstatus",
  "applicantbirthcountry",
  "applicantcitycountry",
  "applicantnationality",
];

const PARTNER_APPLICANT_ADDITIONAL_STUDENT_LABELS: Record<string, string> = {
  applicantdependents: "Applicant Dependents",
  applicantmaritalstatus: "Applicant Marital Status",
  applicantbirthcountry: "Applicant Birth Country",
  applicantcitycountry: "Applicant City Country",
  applicantnationality: "Applicant Nationality",
};

const getPartnerApplicantAdditionalRows = (
  studentDetails: any[] = []
): FormattedItem[] => {
  const detailsByKey = new Map<string, any>();
  studentDetails.filter(Boolean).forEach((field: any) => {
    const key = String(field?.name || "").toLowerCase();
    if (key) detailsByKey.set(key, field);
  });

  return PARTNER_APPLICANT_ADDITIONAL_STUDENT_KEYS
    .map((key) => {
      const field = detailsByKey.get(key);
      if (!field) return null;
      return {
        key,
        label: PARTNER_APPLICANT_ADDITIONAL_STUDENT_LABELS[key],
        value: field?.value ?? "",
      } as FormattedItem;
    })
    .filter(Boolean) as FormattedItem[];
};

const formatSummaryDateDDMMYYYY = (value: unknown): string | number | boolean | undefined => {
  if (value === null || value === undefined || value === "") return value as any;
  const raw = String(value).trim();
  const ymdMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymdMatch) return `${ymdMatch[3]}-${ymdMatch[2]}-${ymdMatch[1]}`;
  if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return value as any;
  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const year = parsed.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatDataHelper = (
  data: RawDataItem[],
  partnerStartedJourney: boolean
): FormattedItem[] => {
  const merged: RawDataItem = {};
  const tempLabels: { [key: string]: string } = {};

  !partnerStartedJourney &&
    data?.forEach((item) => {
      if (
        item &&
        Object.prototype.hasOwnProperty.call(item, "formFieldKey")
      ) {
        const key = String((item as any).formFieldKey || "").toLowerCase();
        if (key) {
          tempLabels[key] = toTitleLabel(String((item as any).formFieldKey));
          merged[key] = (item as any).formFieldValue;
        }
        return;
      }
      Object.entries(item).forEach(([key, value]) => {
        tempLabels[key.toLowerCase()] = toTitleLabel(key);
        merged[key.toLowerCase()] = value;
      });
    });

  const formattedItems: FormattedItem[] = [];

  Object.entries(merged).forEach(([key, value]) => {
    if (key.includes("verified")) return;

    let isVerified = false;
    const isApplicantEmailKey =
      key === "email" ||
      key === "studentdetailemail" ||
      key === "student_detail_email" ||
      key === "studentdetail_email";

    if (key === "phone_number" && isStrictVerifiedFlag(merged?.["ismobileverified"])) {
      isVerified = true;
    } else if (isApplicantEmailKey) {
      const iso = isStrictVerifiedFlag(merged?.["isemailverified"]);
      const addrVal =
        merged["emailaddressverifiedflag"] ??
        merged["email_address_verified_flag"];
      const hasAddrFlag =
        Object.prototype.hasOwnProperty.call(merged, "emailaddressverifiedflag") ||
        Object.prototype.hasOwnProperty.call(
          merged,
          "email_address_verified_flag"
        );
      const addr = isStrictVerifiedFlag(addrVal);
      // If backend sends an explicit address-verified flag, require it too so we do not show
      // "Verified" when only isEmailVerified is out of sync with actual verification.
      isVerified = iso && (!hasAddrFlag || addr);
    } else {
      const verifyKeyVariants: string[] = [
        `is${key}verified`,
        `${key}verified`,
        `${key}_verified`,
        `${key}verified`,
      ];
      for (const vKey of verifyKeyVariants) {
        if (vKey in merged) {
          const v = merged[vKey];
          isVerified = isStrictVerifiedFlag(v);
          break;
        }
      }
    }

    const label = tempLabels[key];
    const displayValue =
      /^(corescoreexpectedon|languagescoreexpectedon)(_\d+)?$/i.test(key)
        ? formatSummaryDateDDMMYYYY(value)
        : value;
    const hasDisplayValue =
      displayValue !== null &&
      displayValue !== undefined &&
      String(displayValue).trim() !== "";
    const isEmailField =
      (key.includes("email") || String(label || "").toLowerCase().includes("email")) &&
      !key.includes("verified");
    const isPhoneField =
      key === "phone_number" ||
      key.includes("phone") ||
      String(label || "").toLowerCase().includes("mobile");
    if (
      label &&
      !label.toLowerCase().includes("terms conditions") &&
      !label.toLowerCase().includes("privacy policy")
    ) {
      formattedItems.push({
        key,
        label,
        value,
        isVerified,
        showNotVerified: hasDisplayValue && !isVerified && (isEmailField || isPhoneField),
      });
    }
  });

  const applicantDetailsOrder = [
    "product",
    "applicanttype",
    "firstname",
    "lastname",
    "phone_number",
    "email",
    "pincode",
  ];

  return formattedItems.sort((a, b) => {
    const aIndex = applicantDetailsOrder.indexOf(String(a.key || "").toLowerCase());
    const bIndex = applicantDetailsOrder.indexOf(String(b.key || "").toLowerCase());
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  });
};

/** Same filtering as `RenderTabContent` for applicant loan rows (exclude co-applicant fields). */
function filterLoanDetailsForSummaryStacked(loanDetails: any): any[] {
  if (!Array.isArray(loanDetails)) return [];
  return loanDetails.filter((field: any) => {
    const fieldIdentifier = String(
      field?.label ?? field?.name ?? ""
    ).toLowerCase();
    return !(
      fieldIdentifier.includes("co-applicant") ||
      fieldIdentifier.includes("co applicant") ||
      fieldIdentifier.includes("coapplicant")
    );
  });
}

type StackedSummaryDetailsCtx = {
  partnerStartedJourney: boolean;
  isLoanDetailsLoading: boolean;
  studentDetails: any;
  addressDetails: any;
  employmentDetails: any;
  academicDetails: any;
  entranceDetails: any;
  loanDetails: any;
  partnerDetails: any;
  fetchUserApplicationData: any;
};

/** Same merge as StudentDetailsTab / AddressDetailsTab for summary completeness checks. */
function mergeFieldArraysSummaryGate(
  primaryFields: any[] = [],
  fallbackFields: any[] = []
): any[] {
  const normalizeKey = (field: any) =>
    String(field?.name ?? field?.key ?? "").toLowerCase();

  const primaryMap = new Map<string, any>();
  primaryFields.forEach((field) => {
    const key = normalizeKey(field);
    if (key) primaryMap.set(key, field);
  });

  const fallbackMap = new Map<string, any>();
  fallbackFields.forEach((field) => {
    const key = normalizeKey(field);
    if (key) fallbackMap.set(key, field);
  });

  const merged = fallbackFields.map((fallbackField) => {
    const key = normalizeKey(fallbackField);
    const primaryField = key ? primaryMap.get(key) : undefined;
    return primaryField
      ? { ...fallbackField, ...primaryField, value: primaryField?.value ?? "" }
      : { ...fallbackField, value: fallbackField?.value ?? "" };
  });

  primaryFields.forEach((primaryField) => {
    const key = normalizeKey(primaryField);
    if (!key || fallbackMap.has(key)) return;
    merged.push({ ...primaryField, value: primaryField?.value ?? "" });
  });

  return merged;
}

function mapStudentDetailsFromVerticalStepSummaryGate(verticalStep: any): any[] {
  if (!verticalStep || !verticalStep?.fields) return [];
  return verticalStep.fields.map((fieldObj: any) => {
    const name = Object.keys(fieldObj)?.[0];
    let value = fieldObj?.[name];
    if (
      name.toLowerCase().includes("dob") &&
      value &&
      /^\d{4}-\d{2}-\d{2}$/.test(value)
    ) {
      const [year, month, day] = value.split("-");
      value = `${day}-${month}-${year}`;
    }
    let label = name.replace(/^studentDetails?/i, "");
    if (/phone_number/i.test(label)) {
      label = "Phone Number";
    } else {
      label = label
        .replace(/_/g, " ")
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (s) => s.toUpperCase())
        .trim();
    }
    return { label, name, value };
  });
}

function hasStudentDetailsFieldsForGate(ctx: StackedSummaryDetailsCtx): boolean {
  const applicantLoanStep = ctx.fetchUserApplicationData?.horizontalSteps?.find(
    (h: any) => Number(h?.horizontalStepId) === 2
  );
  const studentDetailsStep = applicantLoanStep?.verticalSteps?.find(
    (v: any) =>
      Number(v?.verticalStepId) === 4 &&
      v?.verticalStepName === "Student Details"
  );

  return Array.isArray(studentDetailsStep?.fields) &&
    studentDetailsStep.fields.length > 4;
}

function hasRequiredFieldsForSummaryGate(
  fetchUserApplicationData: any,
  horizontalStepId: number,
  verticalStepId: number,
  keys: string[],
  checkNonZero = false
): boolean {
  const horizontalStep = fetchUserApplicationData?.horizontalSteps?.find(
    (h: any) => Number(h?.horizontalStepId) === horizontalStepId
  );
  const fields = horizontalStep?.verticalSteps?.find(
    (v: any) => Number(v?.verticalStepId) === verticalStepId
  )?.fields;

  return keys.every((key) => {
    const field = (fields || []).find(
      (item: any) =>
        getSummaryFieldName(item).toLowerCase() === key.toLowerCase()
    );
    const fieldName = getSummaryFieldName(field);
    const value =
      field?.[fieldName] ?? field?.value ?? field?.formFieldValue ?? field?.fieldValue;
    if (value === null || value === undefined || String(value).trim() === "") {
      return false;
    }
    if (!checkNonZero) return true;
    const numericValue = Number(String(value).replace(/[,\s₹]/g, ""));
    return Number.isFinite(numericValue) ? numericValue !== 0 : true;
  });
}

function mapAddressFieldsSummaryGate(fields: any[], prefix: string): any[] {
  if (!fields) return [];
  return fields
    .filter((fieldObj: any) => Object.keys(fieldObj)[0].startsWith(prefix))
    .map((fieldObj: any) => {
      const name = Object.keys(fieldObj)[0];
      const value = fieldObj[name];
      let label = name.replace(new RegExp(`^${prefix}`), "");
      label = label.replace(/([A-Z])/g, " $1").trim();
      label = label.replace(/(\D)(\d+)$/, "$1 $2");
      label = label.replace(/^./, (str) => str.toUpperCase());
      label = label
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
      return { label, name, value };
    });
}

function getStudentDetailsMergedForGate(ctx: StackedSummaryDetailsCtx): any[] {
  const fallback = Array.isArray(ctx.studentDetails) ? ctx.studentDetails : [];
  const fetchUser = ctx.fetchUserApplicationData;
  if (!fetchUser || ctx.partnerStartedJourney) {
    return fallback;
  }
  const applicantLoanStep = fetchUser.horizontalSteps?.find(
    (h: any) => h.horizontalStepName === "Applicant & Loan Details"
  );
  const studentDetailsStep = applicantLoanStep?.verticalSteps?.find(
    (v: any) => v.verticalStepName === "Student Details"
  );
  if (studentDetailsStep) {
    const mapped = mapStudentDetailsFromVerticalStepSummaryGate(
      studentDetailsStep
    );
    return mergeFieldArraysSummaryGate(mapped, fallback);
  }
  return mergeFieldArraysSummaryGate([], fallback);
}

function getAddressDetailsMergedForGate(
  ctx: StackedSummaryDetailsCtx
): { currentAddress: any[]; permanentAddress: any[] } {
  const fallback = ctx.addressDetails || {
    currentAddress: [],
    permanentAddress: [],
  };
  const fetchUser = ctx.fetchUserApplicationData;
  if (!fetchUser || ctx.partnerStartedJourney) {
    return {
      currentAddress: fallback.currentAddress || [],
      permanentAddress: fallback.permanentAddress || [],
    };
  }
  const applicantLoanStep = fetchUser.horizontalSteps?.find(
    (h: any) => h.horizontalStepName === "Applicant & Loan Details"
  );
  const addressDetailsStep = applicantLoanStep?.verticalSteps?.find(
    (v: any) => v.verticalStepName === "Address Details"
  );
  const stepFields = addressDetailsStep?.fields || [];
  if (addressDetailsStep) {
    const mappedCurrent = mapAddressFieldsSummaryGate(stepFields, "current");
    const mappedPermanent = mapAddressFieldsSummaryGate(stepFields, "permanent");
    return {
      currentAddress: mergeFieldArraysSummaryGate(
        mappedCurrent,
        fallback.currentAddress || []
      ),
      permanentAddress: mergeFieldArraysSummaryGate(
        mappedPermanent,
        fallback.permanentAddress || []
      ),
    };
  }
  return {
    currentAddress: mergeFieldArraysSummaryGate(
      [],
      fallback.currentAddress || []
    ),
    permanentAddress: mergeFieldArraysSummaryGate(
      [],
      fallback.permanentAddress || []
    ),
  };
}

/**
 * True when the summary row would show real field rows — not only the
 * "No details available." empty state (aligned with `RenderTabContent` + tab components).
 */
function stackedSummaryStepHasRenderableDetails(
  applicationLabel: string,
  verticalStep: VerticalStep,
  ctx: StackedSummaryDetailsCtx
): boolean {
  if (ctx.isLoanDetailsLoading) return true;

  const partner = ctx.partnerStartedJourney;
  const fields = verticalStep?.fields || [];
  const formattedLen = formatDataHelper(fields, partner).length;
  const stepName = verticalStep?.verticalStepName;

  if (applicationLabel === "Eligibility Check") {
    if (partner) {
      if (stepName === "Verify Yourself and Provide Consent") {
        const d = ctx.partnerDetails?.applicantDetails;
        return (Array.isArray(d) && d.length > 0) || formattedLen > 0;
      }
      if (
        stepName === "Provide Course Details" ||
        stepName === "Provide Loan Details"
      ) {
        const d = ctx.partnerDetails?.applicationDetails;
        return (Array.isArray(d) && d.length > 0) || formattedLen > 0;
      }
    }
    return formattedLen > 0;
  }

  if (applicationLabel === "Applicant & Loan Details") {
    if (stepName === "Student Details") {
      return hasStudentDetailsFieldsForGate(ctx);
    }
    if (stepName === "Address Details") {
      const addr = getAddressDetailsMergedForGate(ctx);
      const hasBoth =
        (addr.currentAddress?.length ?? 0) > 0 &&
        (addr.permanentAddress?.length ?? 0) > 0;
      return hasBoth || formattedLen > 0;
    }
    if (stepName === "Employment Details") {
      const arr = ctx.employmentDetails;
      return (Array.isArray(arr) && arr.length > 0) || formattedLen > 0;
    }
    if (stepName === "Academic Details") {
      const arr = ctx.academicDetails;
      return (Array.isArray(arr) && arr.length > 0) || formattedLen > 0;
    }
    if (stepName === "Entrance Exam Details") {
      const e = ctx.entranceDetails;
      const hasObj =
        e != null &&
        typeof e === "object" &&
        Object.keys(e as object).length > 0;
      return hasObj || formattedLen > 0;
    }
    if (stepName === "Loan Details" || stepName === "Co-Applicant Details") {
      const f = filterLoanDetailsForSummaryStacked(ctx.loanDetails);
      return f.length > 0 || formattedLen > 0;
    }
    return formattedLen > 0;
  }

  return formattedLen > 0;
}

function coApplicantSummaryBlockHasDetails(stable: any, live: any): boolean {
  const src = live ?? stable;
  if (src == null) return false;
  if (Array.isArray(src)) {
    return src.some((entry: any) => {
      const coKey = Object.keys(entry || {}).find((k) =>
        /^coapplicant\d+$/i.test(k)
      );
      const fields = coKey ? (entry as any)[coKey] : [];
      return Array.isArray(fields) && fields.length > 0;
    });
  }
  if (typeof src === "object") {
    return Object.keys(src).some((k) => {
      if (!/^coapplicant\d+$/i.test(k)) return false;
      const fields = (src as any)[k];
      return Array.isArray(fields) && fields.length > 0;
    });
  }
  return false;
}

const SUMMARY_SKIP_LABELS: string[] = [
  "University Country Id",
  "University Campus Id",
  "University Not In List",
  "Institute Code",
  "Institute Not In List",
  "Course Code",
  "Course Name Not In List",
];

const SUMMARY_RUPEE_FIELDS: string[] = [
  "Loan Amount",
  "Total Cost",
  "Monthly Obligation",
];

type ProcessioLoanOverride = {
  loanLoanAmount?: string | number;
  loanLoanTenure?: string | number;
  loanRequestedRoi?: string | number;
  loanDateOfDisbursement?: string;
} | null;

let latestProcessioLoanOverride: ProcessioLoanOverride = null;
let latestApplicantAdditionalDisplayOptions: {
  loanTenureOptions: Array<{ value: string; label: string; id?: string | number }>;
  maritalStatusOptions: Array<{ value: string; label: string; id?: string | number }>;
} = {
  loanTenureOptions: [],
  maritalStatusOptions: [],
};

const formatProcessioDisbursementDate = (raw: unknown) => {
  if (!raw) return "";
  const rawValue = String(raw).trim();
  const ymdMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymdMatch) {
    return `${ymdMatch[3]}-${ymdMatch[2]}-${ymdMatch[1]}`;
  }
  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) return String(raw);
  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const year = parsed.getFullYear();
  return `${day}-${month}-${year}`;
};

const findApplicantAdditionalOptionLabel = (
  options: Array<{ value: string; label: string; id?: string | number }>,
  value: unknown
): string | number | undefined => {
  const currentValue = String(value ?? "").trim().toLowerCase();
  if (!currentValue) return undefined;
  return (
    options.find((option) => {
      return (
        String(option?.value ?? "").trim().toLowerCase() === currentValue ||
        String(option?.id ?? "").trim().toLowerCase() === currentValue ||
        String(option?.label ?? "").trim().toLowerCase() === currentValue
      );
    })?.label ?? (typeof value === "number" ? value : String(value ?? ""))
  );
};

function renderSummaryFormattedFieldGrid(
  formattedData: FormattedItem[],
  TypedDetailRow: DetailRowType
): React.ReactNode {
  return formattedData.map((field: FormattedItem, subIndex: number) => {
    const fieldLabelLower = field?.label?.toLowerCase();
    const productValueMap: { [key: string]: string } = {
      SLI: "Student Loan International",
    };

    const shouldSkip = SUMMARY_SKIP_LABELS.some(
      (label) => label?.toLowerCase() === fieldLabelLower
    );
    if (shouldSkip) return null;

    const isRupeeField = SUMMARY_RUPEE_FIELDS.some(
      (r) => r?.toLowerCase() === fieldLabelLower
    );

    const baseValue = field?.value;
    const mappedValue =
      fieldLabelLower === "product" && typeof baseValue === "string"
        ? productValueMap[baseValue.trim().toUpperCase()] ?? baseValue
        : baseValue;

    let updatedValue = mappedValue ?? "";
    if (isRupeeField && mappedValue != null) {
      const numericValue = Number(String(mappedValue).replace(/,/g, ""));
      if (!isNaN(numericValue)) {
        updatedValue = new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
          .format(numericValue)
          .replace("₹", "₹\u00A0");
      }
    }

    // Reusable style for the headers
      const headerStyle = {
        fontSize: "15px",
        fontWeight: 600,
        color: "#6f6b7d",
        mb: 1,
      };

    const applicantFields = formattedData
      .filter((item) => item.label.startsWith('Applicant'))
      .map((item) =>
        item.label === "Applicant Marital Status"
          ? {
              ...item,
              value: findApplicantAdditionalOptionLabel(
                latestApplicantAdditionalDisplayOptions.maritalStatusOptions,
                item.value
              ),
            }
          : item
      );

    const processioLoanValueByLabel: Record<string, string | number | undefined> = {
      "Loan Loan Amount": latestProcessioLoanOverride?.loanLoanAmount,
      "Loan Loan Tenure": latestProcessioLoanOverride?.loanLoanTenure,
      "Loan Requested Roi": latestProcessioLoanOverride?.loanRequestedRoi,
      "Loan Date Of Disbursement": formatProcessioDisbursementDate(
        latestProcessioLoanOverride?.loanDateOfDisbursement
      ),
    };
    const loanFields = formattedData
      .filter((item) => item.label.startsWith('Loan'))
      .map((item) => {
        const overrideValue = processioLoanValueByLabel[item.label];
        const resolvedValue = overrideValue === undefined ||
          overrideValue === null ||
          overrideValue === ""
          ? item.value
          : overrideValue;
        if (item.label === "Loan Loan Tenure") {
          return {
            ...item,
            value: findApplicantAdditionalOptionLabel(
              latestApplicantAdditionalDisplayOptions.loanTenureOptions,
              resolvedValue
            ),
          };
        }
        if (item.label === "Loan Date Of Disbursement") {
          return {
            ...item,
            value: formatProcessioDisbursementDate(resolvedValue),
          };
        }
        return resolvedValue === item.value ? item : { ...item, value: resolvedValue };
      });
 
    const isApplicantField = applicantFields.some(
      (item) => item.label === field.label
    );

    const isLoanField = loanFields.some((item) => item.label === field.label);
    const hasGroupedApplicantLoanFields =
      applicantFields.length > 0 || loanFields.length > 0;
    return (
      <>
        {subIndex === 0 && hasGroupedApplicantLoanFields && (
          <>
            {applicantFields.length > 0 && (
              <>
                <Grid item xs={12}>
                  <SummarySectionHeader title="Applicant Details" />
                  {/* <Typography sx={headerStyle}>Applicant Details</Typography> */}
                </Grid>

                {applicantFields.map((field, index) => (
                  <Grid item xs={12} sm={6} md={4} key={`app-${index}`}>
                    <TypedDetailRow
                      item={
                        (field as any)?.label == 'Applicant Dependents'
                          ? { ...field, label: 'No of Dependents' }
                          : (field as any)?.label === 'Applicant Nationality'
                            ? { ...field, label: 'Nationality' }
                            : (field as any)?.label ===
                                'Applicant Marital Status'
                              ? { ...field, label: 'Marital Status' }
                              : (field as any)?.label ===
                                  'Applicant Birth Country'
                                ? { ...field, label: 'Country Of Birth' }
                                : (field as any)?.label ===
                                    'Applicant City Country'
                                  ? { ...field, label: 'City Of Birth' }
                                  : field
                      }
                    />
                  </Grid>
                ))}
              </>
            )}

            {loanFields.length > 0 && (
              <>
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <SummarySectionHeader title="Loan Details" />
                  {/* <Typography sx={headerStyle}>Loan Details</Typography> */}
                </Grid>

                {loanFields.map((field, index) => (
                  <Grid item xs={12} sm={6} md={4} key={`loan-${index}`}>
                    <TypedDetailRow
                      item={
                        (field as any)?.label === 'Loan Loan Amount'
                          ? { ...field, label: 'Loan Amount' }
                          : (field as any)?.label === 'Loan Loan Tenure'
                            ? { ...field, label: 'Loan Tenure' }
                            : (field as any)?.label === 'Loan Requested Roi'
                              ? { ...field, label: 'Requested ROI' }
                              : (field as any)?.label ===
                                  'Loan Date Of Disbursement'
                                ? {
                                    ...field,
                                    label: 'Expected date of disbursement',
                                  }
                                : field
                      }
                    />
                  </Grid>
                ))}
              </>
            )}
          </>
        )}
        {!isApplicantField && !isLoanField && (
          <Grid item xs={12} sm={6} md={4} key={subIndex}>
            <TypedDetailRow
              item={{
                ...field,
                value: updatedValue,
              }}
            />
          </Grid>
        )}
      </>
    );
  });
}

const InnerDetailsTabs: React.FC<InnerDetailsTabsProps> = React.memo(
  ({ innerData, activeTabIndex, activeHandleChange, isXsOrSm }) => (
    <Tabs
      value={activeTabIndex}
      onChange={activeHandleChange}
      variant={isXsOrSm ? "scrollable" : "standard"}
      {...(isXsOrSm && {
        scrollButtons: "auto",
        allowScrollButtonsMobile: false,
      })}
      sx={{
        flexGrow: 1,
        ...(isXsOrSm
          ? {
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTabs-flexContainer": { gap: "8px" },
              "& .MuiTabs-scroller": { paddingBottom: "1px" },
              mb: 3,
            }
          : {
              mb: 2,
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTabs-indicator": { backgroundColor: "#00b3b5" },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                color: "#616161",
                "&.Mui-selected": { color: "#00b3b5" },
              },
            }),
      }}
    >
      {innerData?.map((item: any, i: any) => (
        <Tab
          key={item?.verticalStepName}
          label={
            isXsOrSm ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {item?.verticalStepName}
              </Box>
            ) : (
              item?.verticalStepName
            )
          }
          {...a11yProps(i)}
          sx={
            isXsOrSm
              ? {
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#616161",
                  padding: "10px 18px",
                  borderRadius: "24px",
                  border: "1px solid #E0E0E0",
                  backgroundColor: "#FFFFFF",
                  transition: "all 0.3s ease",
                  "&.Mui-selected": {
                    color: "#00b3b5",
                    backgroundColor: "rgb(0 179 181 / 10%)",
                    borderColor: "#00b3b5",
                  },
                  "&.MuiTab-root": { margin: 0, padding: "10px 18px" },
                }
              : {}
          }
        />
      ))}
    </Tabs>
  )
);

// it will render the content for a specific vertical step tab
const RenderTabContent: React.FC<{
  applicationLabel: string;
  verticalStep: VerticalStep;
  isMobile?: boolean;
  isLoanDetailsLoading: boolean;
  designationsVal?:any;
  pageType?: string;
}> = ({
  applicationLabel,
  verticalStep,
  isMobile = false,
  isLoanDetailsLoading,
  designationsVal,
  pageType = "DEFAULT",
}) => {
  const {
    studentDetails,
    addressDetails,
    employmentDetails,
    companyAddressDetails,
    academicDetails,
    entranceDetails,
    loanDetails,
    expenseDetails,
    referenceDetails,
    collateralDetails,
    partnerDetails,
    educationGap
  } = useSelector((state: RootState) => state?.applicationLoanDetailsData);
  const { fetchUserApplicationData } = useSelector(
    (state: RootState) => state?.stepper
  );
  const TypedDetailRow = DetailRow as DetailRowType;

  const isEligibilityDetails = applicationLabel === "Eligibility Check";
  const isApplicantLoanDetails = applicationLabel === "Applicant & Loan Details";
  const isAdditionalDetails = applicationLabel === "Additional Details & Documents";

  const consentDetailsTabs = {
    "Verify Yourself and Provide Consent": "isProvideConsentDetailsTab",
    "Provide Course Details": "isProvideCourseDetailsTab",
    "Provide Loan Details": "isProvideLoanDetailsTab",
  };

  const detailsTabs = {
    "Student Details": "isStudentDetailsTab",
    "Address Details": "isAddressDetailsTab",
    "Employment Details": "isEmploymentDetailsTab",
    "Academic Details": "isAcademicDetailsTab",
    "Entrance Exam Details": "isEntranceExamDetailsTab",
    "Loan Details": "isLoanDetailsTab",
    "Co-Applicant Details": "isLoanDetailsTab",
  };

  const additionalDetailsTabs: Record<string, string> = {
    "Applicant Additional Details": "isApplicantAdditionalDetailsTab",
    "Funds and Expenses": "isExpenseDetailsTab",
    [FUNDS_AND_EXPENSES_STEP_NAME]: "isExpenseDetailsTab",
    [REFERENCE_AND_COLLATERAL_STEP_NAME]: "isReferenceAndCollateralTab",
    "Reference Details": "isReferenceAndCollateralTab",
    "Collateral Details": "isReferenceAndCollateralTab",
  };

  let activeTab: string | undefined;
  if (isEligibilityDetails) {
    activeTab =
      consentDetailsTabs[
        verticalStep.verticalStepName as keyof typeof consentDetailsTabs
      ];
  } else if (isApplicantLoanDetails) {
    activeTab =
      detailsTabs[verticalStep.verticalStepName as keyof typeof detailsTabs];
  } else if (isAdditionalDetails) {
    activeTab = additionalDetailsTabs[verticalStep.verticalStepName];
  }

  const isProvideConsentDetailsTab = activeTab === "isProvideConsentDetailsTab";
  const isProvideCourseDetailsTab = activeTab === "isProvideCourseDetailsTab";
  const isProvideLoanDetailsTab = activeTab === "isProvideLoanDetailsTab";
  const isStudentDetailsTab = activeTab === "isStudentDetailsTab";
  const isAddressDetailsTab = activeTab === "isAddressDetailsTab";
  const isEmploymentDetailsTab = activeTab === "isEmploymentDetailsTab";
  const isAcademicDetailsTab = activeTab === "isAcademicDetailsTab";
  const isEntranceExamDetailsTab = activeTab === "isEntranceExamDetailsTab";
  const isLoanDetailsTab = activeTab === "isLoanDetailsTab";
  const isApplicantAdditionalDetailsTab =
    activeTab === "isApplicantAdditionalDetailsTab";
  const isExpenseDetailsTab = activeTab === "isExpenseDetailsTab";
  const isReferenceAndCollateralTab =
    activeTab === "isReferenceAndCollateralTab";
  const isReferenceDetailsOnlyTab =
    verticalStep.verticalStepName === REFERENCE_DETAILS_STEP_NAME;
  const isCollateralDetailsOnlyTab =
    verticalStep.verticalStepName === COLLATERAL_DETAILS_STEP_NAME;

  const filteredLoanDetails = Array.isArray(loanDetails)
    ? loanDetails.filter((field: any) => {
        const fieldIdentifier = String(
          field?.label ?? field?.name ?? ""
        ).toLowerCase();
        return !(
          fieldIdentifier.includes("co-applicant") ||
          fieldIdentifier.includes("co applicant") ||
          fieldIdentifier.includes("coapplicant")
        );
      })
    : loanDetails;

  const isCustomTab =
    isProvideConsentDetailsTab ||
    isProvideCourseDetailsTab ||
    isProvideLoanDetailsTab ||
    isStudentDetailsTab ||
    isAddressDetailsTab ||
    isEmploymentDetailsTab ||
    isAcademicDetailsTab ||
    isEntranceExamDetailsTab ||
    isLoanDetailsTab ||
    isApplicantAdditionalDetailsTab ||
    isExpenseDetailsTab ||
    isReferenceAndCollateralTab;
  
  const renderContent = () => {
    if (isCustomTab && isLoanDetailsLoading) {
      return tabContentSkeleton();
    }

    if (isCustomTab) {
      if (isStudentDetailsTab) {
        return !studentDetails ? (
          tabContentSkeleton()
        ) : (
          <StudentDetailsTab
            studentDetails={studentDetails}
            isMobile={isMobile}
            pageType={pageType}
          />
        );
      } else if (isAddressDetailsTab) {
        return !addressDetails ? (
          tabContentSkeleton()
        ) : (
          <AddressDetailsTab
            addressDetails={addressDetails}
            isMobile={isMobile}
            pageType={pageType}
          />
        );
      } else if (isEmploymentDetailsTab) {
        return isLoanDetailsLoading || employmentDetails === undefined ? (
          tabContentSkeleton()
        ) : (
          <EmploymentDetailsTab
            employmentDetails={employmentDetails}
            companyAddressDetails={companyAddressDetails}
            isMobile={isMobile}
            designationsVal={designationsVal}
            pageType={pageType}
          />
        );
      } else if (isAcademicDetailsTab) {
        return !academicDetails ? (
          tabContentSkeleton()
        ) : (
          <AcademicDetailsTab
            academicDetails={academicDetails}
            isMobile={isMobile}
            educationGap={educationGap}
            pageType={pageType}
          />
        );
      } else if (isEntranceExamDetailsTab) {
        return !entranceDetails ? (
          tabContentSkeleton()
        ) : (
          <EntranceExamDetailsTab
            entranceDetails={entranceDetails}
            isMobile={isMobile}
            pageType={pageType}
          />
        );
      } else if (isLoanDetailsTab) {
        return !filteredLoanDetails ? (
          tabContentSkeleton()
        ) : (
          <LoanDetailsTab
            loanDetails={filteredLoanDetails}
            isMobile={isMobile}
            pageType={pageType}
          />
        );
      } else if (isApplicantAdditionalDetailsTab) {
        let applicantExtra = formatDataHelper(
          verticalStep?.fields || [],
          false
        );
        if (fetchUserApplicationData?.partnerStartedJourney) {
          applicantExtra = [
            ...applicantExtra.filter(
              (item) =>
                !PARTNER_APPLICANT_ADDITIONAL_STUDENT_KEYS.includes(
                  String(item?.key || "").toLowerCase()
                )
            ),
            ...getPartnerApplicantAdditionalRows(studentDetails),
          ];
        }
        if (!applicantExtra?.length) {
          return isLoanDetailsLoading ? (
            tabContentSkeleton()
          ) : (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                No details available.
              </Typography>
            </Grid>
          );
        }
        return renderSummaryFormattedFieldGrid(
          applicantExtra,
          TypedDetailRow
        );
      } else if (isExpenseDetailsTab) {
        const fromFields = fieldsToExpenseDetailsRows(verticalStep?.fields);
        const expensePayload =
          fetchUserApplicationData?.partnerStartedJourney
            ? expenseDetails
            : fromFields.length > 0 ? fromFields : expenseDetails;
        if (!expensePayload || expensePayload.length === 0) {
          return isLoanDetailsLoading ? (
            tabContentSkeleton()
          ) : (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                No details available.
              </Typography>
            </Grid>
          );
        }
        return (
          <ExpenseDetailsTab
            expenseDetails={expensePayload}
            isMobile={isMobile}
          />
        );
      } else if (isReferenceAndCollateralTab) {
        const additionalDetailsStep = fetchUserApplicationData?.horizontalSteps?.find(
          (step: any) =>
            Number(step?.horizontalStepId ?? step?.id) === 4 ||
            String(step?.horizontalStepName || step?.stepLabel || "").trim() ===
              "Additional Details & Documents"
        );
        const relatedFields = (additionalDetailsStep?.verticalSteps || [])
          .filter((step: any) =>
            [
              REFERENCE_AND_COLLATERAL_STEP_NAME,
              REFERENCE_DETAILS_STEP_NAME,
              COLLATERAL_DETAILS_STEP_NAME,
            ].includes(String(step?.verticalStepName || step?.stepLabel || "").trim())
          )
          .flatMap((step: any) => step?.fields || []);
        const flat = mergeVerticalStepFields(
          relatedFields.length > 0 ? relatedFields : verticalStep?.fields
        );
        const refFromFields = fieldsToReferenceDetailsFromFlat(flat);
        const colFromFields = fieldsToCollateralDetailsFromFlat(flat);
        const refPayload =
          fetchUserApplicationData?.partnerStartedJourney
            ? referenceDetails
            : refFromFields.length > 0 ? refFromFields : referenceDetails;
        const colPayload =
          fetchUserApplicationData?.partnerStartedJourney
            ? collateralDetails
            : colFromFields.length > 0 ? colFromFields : collateralDetails;
        if (isReferenceDetailsOnlyTab) {
          return (
            <ReferenceDetailsTab
              referenceDetails={refPayload}
              isMobile={isMobile}
            />
          );
        }
        if (isCollateralDetailsOnlyTab) {
          return (
            <CollateralDetailsTab
              collateralDetails={colPayload}
              isMobile={isMobile}
            />
          );
        }
        return (
          <>
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: "15px",
                  fontWeight: 700,
                  color: "#6f6b7d",
                  mb: 1,
                }}
              >
                Reference Details
              </Typography>
            </Grid>
            <ReferenceDetailsTab
              referenceDetails={refPayload}
              isMobile={isMobile}
            />
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: "15px",
                  fontWeight: 700,
                  color: "#6f6b7d",
                  mb: 1,
                }}
              >
                Collateral Details
              </Typography>
            </Grid>
            <CollateralDetailsTab
              collateralDetails={colPayload}
              isMobile={isMobile}
            />
          </>
        );
      } else if (
        isProvideConsentDetailsTab &&
        fetchUserApplicationData?.partnerStartedJourney
      ) {
        return !partnerDetails ? (
          tabContentSkeleton()
        ) : (
          <ProvideConsentDetailsTab
            partnerDetails={partnerDetails?.applicantDetails}
            partnerApplicationDetails={partnerDetails?.applicationDetails}
            isMobile={isMobile}
          />
        );
      } else if (
        isProvideCourseDetailsTab &&
        fetchUserApplicationData?.partnerStartedJourney
      ) {
        return !partnerDetails ? (
          tabContentSkeleton()
        ) : (
          <ProvideCourseDetailsTab
            partnerDetails={partnerDetails?.applicationDetails}
            isMobile={isMobile}
          />
        );
      } else if (
        isProvideLoanDetailsTab &&
        fetchUserApplicationData?.partnerStartedJourney
      ) {
        return !partnerDetails ? (
          tabContentSkeleton()
        ) : (
          <ProvideLoanDetailsTab
            partnerDetails={partnerDetails?.applicationDetails}
            isMobile={isMobile}
          />
        );
      }
    }

    const formattedData = formatDataHelper(
      verticalStep?.fields,
      fetchUserApplicationData?.partnerStartedJourney
    );

    if (formattedData?.length === 0) {
      return isLoanDetailsLoading ? (
        tabContentSkeleton()
      ) : (
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            No details available.
          </Typography>
        </Grid>
      );
    }

    return renderSummaryFormattedFieldGrid(formattedData, TypedDetailRow);
  };

  return (
    <Box>
      {isStudentDetailsTab && !isMobile && (
        <Typography
          sx={{
            pb: 3,
            fontSize: "13px",
            fontWeight: 600,
            color: "#6f6b7d",
          }}
        >
          Verification & Consent Details
        </Typography>
      )}

      <Grid container rowSpacing={2} columnSpacing={isMobile ? 2 : 4}>
        {renderContent()}
      </Grid>
    </Box>
  );
};

// bottomsheet mobile inner view
const ApplicationDetailsTabs: React.FC<ApplicationDetailsTabsProps> = ({
  application,
  tabIndex: propTabIndex,
  handleChange: propHandleChange,
  title,
  completionRate,
  onClose,
  isLoanDetailsLoading,
  isXsOrSm,
  coApplicantDetails,
  coApplicantCompletionRate,
  designationsVal,
}) => {
  const activeTabIndex = propTabIndex;
  const activeHandleChange = propHandleChange;

  const defaultApplicantLoanDetailsTabs: VerticalStep[] = [
    { verticalStepName: "Student Details", fields: [] },
    { verticalStepName: "Address Details", fields: [] },
    { verticalStepName: "Employment Details", fields: [] },
    { verticalStepName: "Academic Details", fields: [] },
    { verticalStepName: "Entrance Exam Details", fields: [] },
    { verticalStepName: "Co-Applicant Details", fields: [] },
  ];
  const mergeWithDefaultTabs = (
    sourceTabs: VerticalStep[],
    defaultTabs: VerticalStep[]
  ): VerticalStep[] => {
    const sourceByName = new Map(
      (sourceTabs || []).map((tab) => [tab?.verticalStepName, tab])
    );
    return defaultTabs.map(
      (defaultTab) =>
        sourceByName.get(defaultTab.verticalStepName) ?? defaultTab
    );
  };

  let innerDataToUse: VerticalStep[] = application?.innerData || [];
  const isCoApplicantSection = application?.label === "Co-Applicant";
  if (application?.label === "Applicant & Loan Details") {
    innerDataToUse = mergeWithDefaultTabs(
      innerDataToUse,
      defaultApplicantLoanDetailsTabs
    ).filter(
      (tab) => !isHiddenApplicantLoanVerticalStep(tab?.verticalStepName)
    );
  } else if (application?.label === "Additional Details & Documents") {
    innerDataToUse = mergeAdditionalDetailsTabs(innerDataToUse);
  }
  const shouldShowCoApplicantTabs =
    isCoApplicantSection && innerDataToUse.length === 0;
  
  return (
    <Box>
      {isXsOrSm && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: "20px", fontWeight: 700, color: "#333" }}
            >
              {title}
            </Typography>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 400, color: "#949393ff" }}
            >
              {title} Related Details
            </Typography>
          </Box>

          {/* <Typography
            sx={{ fontSize: "15px", fontWeight: 500, color: "#00b3b5" }}
          >
            {isCoApplicantSection
              ? `${coApplicantCompletionRate}%`
              : completionRate}
          </Typography> */}
        </Box>
      )}

      <Box>
        {shouldShowCoApplicantTabs ? (
          !coApplicantDetails ? (
            referenceDetailsSkeleton()
          ) : (
            <CoApplicantTabs
              coApplicant={coApplicantDetails}
              pageType="SUMMARY"
              designationsVal={designationsVal}
            />
          )
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                ...(isXsOrSm
                  ? {}
                  : { borderBottom: 1, borderColor: "divider" }),
              }}
            >
              {/* <InnerDetailsTabs
                innerData={innerDataToUse}
                activeTabIndex={activeTabIndex}
                activeHandleChange={activeHandleChange}
                isXsOrSm={isXsOrSm}
              /> */}
            </Box>

            {/* {innerDataToUse?.map((verticalStep: VerticalStep, i: number) => (
              <TabPanel value={activeTabIndex} index={i} key={i}>
                <RenderTabContent
                  applicationLabel={application?.label}
                  verticalStep={verticalStep}
                  isMobile={isXsOrSm}
                  isLoanDetailsLoading={isLoanDetailsLoading}
                  designationsVal={designationsVal}
                  pageType="SUMMARY"
                />
              </TabPanel>
            ))} */}
          </>
        )}
      </Box>

      <Button
        variant="contained"
        sx={{
          width: "100%",
          backgroundColor: "#00b3b5",
          color: "#fff",
          "&:hover": { backgroundColor: "#00b3b5" },
          fontWeight: "600",
          fontSize: "14px",
          borderRadius: 2,
          textTransform: "capitalize",
          py: 1,
          mt: 1,
        }}
        onClick={onClose}
      >
        Close
      </Button>
    </Box>
  );
};

interface ApplicationDetailsProps {
  dataView?: string;
}

// --- Main Component ---
const ApplicationDetails: React.FC<ApplicationDetailsProps> = ({ dataView }) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = useState<CombinedStep[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBreDataFlag, setIsBreDataFlag] = useState(false);
  const [isViewProvisionalOfferModalOpen, setIsViewProvisionalOfferModalOpen] = useState(false);
  const [isOpenPendingModal, setOpenPendingModal] = useState(false);
  const [isLoanDetailsLoading, setIsLoanDetailsLoading] = useState(false);
  const applicantLoanTabDetailsFetchedRef = useRef("");
  const partnerAdditionalDetailsRef = useRef<any>({
    key: "",
    promise: null,
    data: null,
  });
  const summaryDataRequestRef = useRef(0);
  const token: any = process.env.REACT_APP_FETCH_TOKEN;
  const applicationNumber = useSelector((state: RootState) => state.stepper.trackingId);
  const { isProvisionalOffer, fetchUserApplicationData } = useSelector((state: RootState) => state?.stepper);
  const actualPartnerStartedJourney = useSelector(
    (state: RootState) => state.stepper.partnerStartedJourney
  );
  const applicationStatus = useSelector(
    (state: RootState) => state.tabs.applicationStatus
  );
  const provisionalOffer = useSelector((state: RootState) => state.stepper.breData);
  const formJsonRefreshNonce = useSelector((state: RootState) => state.stepper.formJsonRefreshNonce);
  const [expandedSection, setExpandedSection] = useState<number | null>(null);
  const defaultExpandedSectionInitializedRef = useRef(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [applicantAdditionalDisplayOptions, setApplicantAdditionalDisplayOptions] =
    useState(latestApplicantAdditionalDisplayOptions);
  latestApplicantAdditionalDisplayOptions = applicantAdditionalDisplayOptions;
  const { userProfile } = useSelector(authSelector);
  const {
    coApplicantDetails,
    coApplicantCompletionRate,
    studentDetails,
    addressDetails,
    employmentDetails,
    academicDetails,
    entranceDetails,
    loanDetails,
    partnerDetails,
  } = useSelector((state: RootState) => state?.applicationLoanDetailsData);
  const horizontalSteps = useSelector(
    (state: RootState) => state.stepper.horizontalSteps
  );
  const summaryApplicationEditActive = useSelector(
    (state: RootState) => state.stepper.summaryApplicationEditActive
  );
  const summaryEditOpenedFromCoApplicantSection = useSelector(
    (state: RootState) => state.stepper.summaryEditOpenedFromCoApplicantSection
  );
  const coApplicantSummaryViewOnly = useSelector(
    (state: RootState) => state.stepper.coApplicantSummaryViewOnly
  );
  useEffect(() => {
    const expandedApplication = data?.[expandedSection ?? -1];
    if (expandedApplication?.label !== "Additional Details & Documents") return;

    const innerDataToUse = mergeAdditionalDetailsTabs(
      expandedApplication?.innerData || []
    );
    const activeVerticalStep =
      innerDataToUse?.[tabIndex]?.verticalStepName ||
      expandedApplication?.innerData?.[tabIndex]?.verticalStepName;
    if (activeVerticalStep !== "Applicant Additional Details") return;

    let active = true;
    const fetchApplicantAdditionalDisplayOptions = async () => {
      try {
        const [systemValuesResponse, maritalStatusResponse] =
          await Promise.all([
            eligibilityCheckServices.getSystemValues(),
            eligibilityCheckServices.getMaritalStatusMaster(),
          ]);
        if (!active) return;

        const systemValues = systemValuesResponse?.data?.data || [];
        const loanTenureOptions = (Array.isArray(systemValues)
          ? systemValues
          : []
        )
          .filter(
            (item: any) =>
              item?.name === "LOAN_TENURE_INMONTH" && item?.active
          )
          .map((item: any) => ({
            value: String(item?.id ?? item?.code ?? ""),
            label: String(item?.value ?? ""),
            id: item?.serialId,
          }));

        const maritalData = maritalStatusResponse?.data?.data || [];
        const maritalStatusOptions = (Array.isArray(maritalData)
          ? maritalData
          : []
        ).map((item: any) => ({
          value: String(
            item?.maritalStatusCode ?? item?.code ?? item?.value ?? ""
          ),
          label: String(
            item?.maritalStatusName ??
              item?.maritalStatusDesc ??
              item?.desc ??
              item?.value ??
              item?.label ??
              item?.code ??
              ""
          ),
          id: item?.serialId,
        }));

        setApplicantAdditionalDisplayOptions({
          loanTenureOptions,
          maritalStatusOptions,
        });
      } catch (error) {
        console.error("Failed to fetch applicant additional display options", error);
      }
    };

    fetchApplicantAdditionalDisplayOptions();
    return () => {
      active = false;
    };
  }, [data, expandedSection, tabIndex]);
  const loanDetailCoApplicantCollapseValue = useSelector(
    (state: RootState) => state.stepper.loanDetailCoApplicantCollapseValue
  );
  const disableAddMoreCoApplicant = useSelector(
    (state: RootState) => state.stepper.disableAddMoreCoApplicant
  );
  const summaryFormSaveInProgress = useSelector(
    (state: RootState) => state.stepper.summaryFormSaveInProgress
  );
  const activeInlineEditCoApplicantNumber = useMemo(() => {
    const m = String(loanDetailCoApplicantCollapseValue || "").match(
      /^CoApplicant(\d+)$/
    );
    return m ? parseInt(m[1], 10) : null;
  }, [loanDetailCoApplicantCollapseValue]);
  const [activeInlineEditCoApplicantDisplayIndex, setActiveInlineEditCoApplicantDisplayIndex] =
    useState<number | null>(null);
  const activeHorizontalIndex = useSelector(
    (state: RootState) => state.stepper.activeHorizontalIndex
  );
  const activeVerticalIndexMap = useSelector(
    (state: RootState) => state.stepper.activeVerticalIndex
  );
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [summaryEditTarget, setSummaryEditTarget] = useState<{
    h: number;
    v: number;
    name: string;
  } | null>(null);
  const pendingSummaryEditActionRef = useRef<(() => void) | null>(null);
  const [summaryEditSwitchAlert, setSummaryEditSwitchAlert] = useState<{
    currentSectionName: string;
    nextSectionName: string;
  } | null>(null);
  const breData = useSelector((state: RootState) => state.stepper.breData);
  const coApplicantDetailsRef = useRef(coApplicantDetails);
  
  // Update ref if we have data
  if (coApplicantDetails) {
    coApplicantDetailsRef.current = coApplicantDetails;
  }
  // Use stable data to prevent unmounting
  const stableCoApplicantDetails =
    coApplicantDetails || coApplicantDetailsRef.current;
  const getCoApplicantDisplayIndex = (coApplicantNumber: number | null) => {
    if (!coApplicantNumber || !Array.isArray(stableCoApplicantDetails)) {
      return null;
    }
    const targetIndex = stableCoApplicantDetails.findIndex((coAppObj: any) =>
      Object.keys(coAppObj || {}).some(
        (key) => key === `coapplicant${coApplicantNumber}`
      )
    );
    return targetIndex >= 0 ? targetIndex + 1 : coApplicantNumber;
  };
  const stackedSequentialSummaryGate = useMemo(() => {
    const defaultApplicantLoanDetailsTabs: VerticalStep[] = [
      { verticalStepName: "Student Details", fields: [] },
      { verticalStepName: "Address Details", fields: [] },
      { verticalStepName: "Employment Details", fields: [] },
      { verticalStepName: "Academic Details", fields: [] },
      { verticalStepName: "Entrance Exam Details", fields: [] },
      { verticalStepName: "Co-Applicant Details", fields: [] },
    ];
    const mergeWithDefaultTabs = (
      sourceTabs: VerticalStep[],
      defaultTabs: VerticalStep[]
    ): VerticalStep[] => {
      const sourceByName = new Map(
        (sourceTabs || []).map((tab) => [tab?.verticalStepName, tab])
      );
      return defaultTabs.map(
        (defaultTab) =>
          sourceByName.get(defaultTab.verticalStepName) ?? defaultTab
      );
    };

    const eligApp = data?.find((s) => s?.label === "Eligibility Check");
    const appApp = data?.find((s) => s?.label === "Applicant & Loan Details");
    const eligSteps: VerticalStep[] = eligApp?.innerData || [];
    const appliSteps: VerticalStep[] = appApp
      ? mergeWithDefaultTabs(
          appApp.innerData || [],
          defaultApplicantLoanDetailsTabs
        )
      : [];

    const ctx: StackedSummaryDetailsCtx = {
      partnerStartedJourney: !!fetchUserApplicationData?.partnerStartedJourney,
      isLoanDetailsLoading,
      studentDetails,
      addressDetails,
      employmentDetails,
      academicDetails,
      entranceDetails,
      loanDetails,
      partnerDetails,
      fetchUserApplicationData,
    };

    const detailFlags: boolean[] = [];
    eligSteps.forEach((step) => {
      detailFlags.push(
        stackedSummaryStepHasRenderableDetails("Eligibility Check", step, ctx)
      );
    });
    appliSteps.forEach((step) => {
      detailFlags.push(
        stackedSummaryStepHasRenderableDetails(
          "Applicant & Loan Details",
          step,
          ctx
        )
      );
    });
    detailFlags.push(
      coApplicantSummaryBlockHasDetails(
        stableCoApplicantDetails,
        coApplicantDetails
      )
    );

    const firstIncompleteIdx = detailFlags.findIndex((ok) => !ok);
    return {
      firstIncompleteIdx,
      eligStepCount: eligSteps.length,
      appliStepCount: appliSteps.length,
    };
  }, [
    data,
    fetchUserApplicationData,
    isLoanDetailsLoading,
    studentDetails,
    addressDetails,
    employmentDetails,
    academicDetails,
    entranceDetails,
    loanDetails,
    partnerDetails,
    stableCoApplicantDetails,
    coApplicantDetails,
  ]);

  useEffect(() => {
    defaultExpandedSectionInitializedRef.current = false;
    setExpandedSection(null);
    setTabIndex(0);
    setIsBottomSheetOpen(false);
  }, [dataView]);

  useEffect(() => {
    if (defaultExpandedSectionInitializedRef.current) return;
    if (!data?.length) return;
    if (expandedSection !== null) return;
    const eligibilityIndex = data.findIndex(
      (section) => section?.label === "Eligibility Check"
    );
    if (eligibilityIndex >= 0) {
      defaultExpandedSectionInitializedRef.current = true;
      setExpandedSection(eligibilityIndex);
    }
  }, [data, expandedSection]);

  const coApplicantTabsEditDisabled = useMemo(() => {
    const { firstIncompleteIdx, eligStepCount, appliStepCount } =
      stackedSequentialSummaryGate;
    if (firstIncompleteIdx < 0) return false;
    const coApplicantGlobalIdx = eligStepCount + appliStepCount;
    return coApplicantGlobalIdx > firstIncompleteIdx;
  }, [stackedSequentialSummaryGate]);

  // it will reset opened tab/bottomsheet if device changes
  useEffect(() => {
    if (isXsOrSm) {
      setExpandedSection(null);
    } else {
      setIsBottomSheetOpen(false);
    }
  }, [isXsOrSm]);
  const [designations, setDesignations] = useState<any[]>([]);
  useEffect(() => {
    const getCoApplicantCompletionRate = async () => {
      try {
        const res =
          await applicationDetailsServices.getCoApplicantCompletionRate(
            applicationNumber!
          );

        const fallbackCompletionRate = res?.data?.copplicantCompletionRate || "";
        let sectionCompletionRate: number | string = fallbackCompletionRate;

        try {
          const completionSummaryResponse =
            await eligibilityCheckServices.getFormCompletionSummary(
              applicationNumber!
            );
          const completionSummaryData =
            completionSummaryResponse?.data?.data ??
            completionSummaryResponse?.data ??
            null;
          const calculatedCompletion = calculateCoApplicantCompletionSummary(
            completionSummaryData,
            [fetchUserApplicationData, horizontalSteps]
          )?.completionPercentage;

          if (calculatedCompletion !== undefined) {
            sectionCompletionRate = calculatedCompletion;
          }
        } catch (summaryError) {
          console.error("Error fetching co-applicant completion summary:", summaryError);
        }

        dispatch(setCoApplicantCompletionRate(sectionCompletionRate || ""));
      } catch (error) {
        console.error("Error fetching completion rate:", error);
      }
    };
    const fetchDesignations = async () => {
      try {
        const response = await eligibilityCheckServices.getDesignations();
        setDesignations(response?.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch designations", error);
      }
    };
    if (applicationNumber) {
      getCoApplicantCompletionRate();
      if(fetchUserApplicationData?.partnerStartedJourney===false){
        fetchDesignations();
      }
      
    }
  }, [applicationNumber, fetchUserApplicationData, horizontalSteps]);

  // helper functions
  const CustomImageIcon: React.FC<{ src: string }> = ({ src }) => (
    <img src={src} alt="step icon" style={{ height: 24, width: 24 }} />
  );

  const stepIcons: { [key: string]: React.ElementType } = {
    "Eligibility Check": CustomImageIcon,
    "Applicant & Loan Details": CustomImageIcon,
    "Provisional Offer Letter": CustomImageIcon,
    "Co-Applicant": CustomImageIcon,
    "Additional Details & Documents": CustomImageIcon,
    "Final Verification & Sanction": CustomImageIcon,
    "Disbursement of Funds": CustomImageIcon,
  };

  const iconSrcMap: { [key: string]: string } = {
    "Eligibility Check": eligibilityCheckIcon,
    "Applicant & Loan Details": applicantAndLoanDetailsIcon,
    "Provisional Offer Letter": provisionalOfferLetterIcon,
    "Co-Applicant": coApplicantIcon,
    "Additional Details & Documents": addtionalDetailsAndDocumentsIcon,
    "Final Verification & Sanction": finalVerificationAndSanctionIcon,
    "Disbursement of Funds": DisbursementOfFundsIcon,
  };

  const memoizedIcons = React.useMemo(() => {
    const icons: { [key: string]: React.FC<any> } = {};
    Object.keys(stepIcons).forEach((label) => {
      const IconComponent = stepIcons[label] || DocumentIcon;
      const iconProps =
        IconComponent === CustomImageIcon ? { src: iconSrcMap[label] } : {};
      icons[label] = (props: any) => (
        <IconComponent {...iconProps} {...props} />
      );
    });
    return icons;
  }, []);

  const fetchApplicantLoanTabDetails = async (newValue: number) => {
    if (newValue === 2) {
      const employmentRes =
        await applicationDetailsServices.getEmploymentDetails(
          fetchUserApplicationData?.applicantId || "",
          applicationNumber || ""
        );
      if (employmentRes && employmentRes.data) {
        const employmentData =
          employmentRes?.data?.data?.EmploymentDetails;
        // Check if data has valid structure (not just empty object {})
        const hasValidStructure =
          employmentData &&
          (employmentData.EmploymentDetails !== undefined ||
            employmentData.CompanyAddressDetails !== undefined);

        dispatch(
          setEmploymentDetails(
            hasValidStructure
              ? employmentData
              : {
                  EmploymentDetails: [],
                  CompanyAddressDetails: [],
                }
          )
        );
      }
    } else if (newValue === 3) {
      const academicRes =
        await applicationDetailsServices.getAcademicDetails(
          fetchUserApplicationData?.applicationId || "",
          applicationNumber || ""
        );
      if (academicRes && academicRes.data) {
        const details =
          academicRes?.data?.data?.["Application&LoanDetails"]
            ?.academicDetails || [];
        dispatch(
          setEducationGap(
            academicRes?.data?.data?.["Application&LoanDetails"]
              ?.educationGap || 0
          )
        );
        dispatch(setAcademicDetails({ academicDetails: details }));
      }
    } else if (newValue === 4) {
      const entranceRes =
        await applicationDetailsServices.getEntranceDetails(
          fetchUserApplicationData?.applicationId,
          fetchUserApplicationData?.applicantId || "",
          applicationNumber || ""
        );
      if (entranceRes && entranceRes?.data) {
        dispatch(setEntranceDetails(entranceRes?.data?.data));
      }
    } else if (newValue === 5) {
      // loan details logic
    }
  };

  const handleChange = async (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setTabIndex(newValue);
    const selected = data[expandedSection ?? -1];

    // Applicant & Loan tab-switch fetches: partner journey only (unchanged).
    if (
      fetchUserApplicationData?.partnerStartedJourney &&
      fetchUserApplicationData?.applicantId &&
      fetchUserApplicationData?.applicationId
    ) {
      if (
        selected &&
        selected?.label === "Applicant & Loan Details" &&
        (fetchUserApplicationData?.applicantId ||
          fetchUserApplicationData?.applicationId)
      ) {
        setIsLoanDetailsLoading(true);
        try {
          await fetchApplicantLoanTabDetails(newValue);
        } catch (err) {
          console.error("Failed to fetch details", err);
        } finally {
          setIsLoanDetailsLoading(false);
        }
      }
    }
  };

  const toggleSection = async (index: number) => {
    const isExpanding = expandedSection !== index;
    setExpandedSection(isExpanding ? index : null);
    if (!isExpanding) return;
    const selected = data[index];
    setIsLoanDetailsLoading(true);

    try {
      // Applicant & Loan summary bundle: partner journey only (existing behaviour).
      if (
        fetchUserApplicationData?.partnerStartedJourney &&
        fetchUserApplicationData?.applicantId &&
        fetchUserApplicationData?.applicationId
      ) {
        const shouldFetchApplicationLoanDetails =
          selected?.label === "Applicant & Loan Details" ||
          selected?.label === "Additional Details & Documents";
        if (
          selected &&
          shouldFetchApplicationLoanDetails &&
          (fetchUserApplicationData?.applicantId ||
            fetchUserApplicationData?.applicationId)
        ) {
          const res =
            await applicationDetailsServices.getApplicationLoanDetails(
              fetchUserApplicationData?.applicantId || "",
              applicationNumber || ""
            );
          if (res && res.data) {
            dispatch(
              setApplicantLoanDetails(
                res?.data?.data?.["Application&LoanDetails"]
              )
            );
          }
          const fetchKey = [
            applicationNumber,
            fetchUserApplicationData?.applicantId,
            fetchUserApplicationData?.applicationId,
          ].join(":");
          if (
            selected?.label === "Applicant & Loan Details" &&
            applicantLoanTabDetailsFetchedRef.current !== fetchKey
          ) {
            applicantLoanTabDetailsFetchedRef.current = fetchKey;
            await Promise.all(
              [2, 3, 4, 5].map((newValue) =>
                fetchApplicantLoanTabDetails(newValue)
              )
            );
          }
        }
      }
    } catch (e) {
      console.error("Failed to fetch section details", e);
    } finally {
      setIsLoanDetailsLoading(false);
    }
  };

  const onToggleHandler = (application: CombinedStep, appIndex: number) => {
    if (application?.label.toLowerCase() === "provisional offer letter") {
      return;
    }
    setTabIndex(0);
    toggleSection(appIndex);
    if (isXsOrSm) {
      setIsBottomSheetOpen(true);
    }
  };

  const handleCloseBottomSheet = () => {
    setIsBottomSheetOpen(false);
    setExpandedSection(null);
  };

  // --- BottomSheet Content ---
  const memoizedBottomSheetContent = useMemo(() => {
    const applicationData = data[expandedSection ?? -1];

    if (!isXsOrSm || !applicationData) {
      return null;
    }

    return (
      <ApplicationDetailsTabs
        application={applicationData}
        tabIndex={tabIndex}
        handleChange={handleChange}
        title={applicationData?.label}
        completionRate={applicationData?.completionRate}
        onClose={handleCloseBottomSheet}
        isLoanDetailsLoading={isLoanDetailsLoading}
        isXsOrSm={isXsOrSm}
        coApplicantDetails={stableCoApplicantDetails}
        coApplicantCompletionRate={coApplicantCompletionRate}
        designationsVal={designations}
      />
    );
  }, [
    isXsOrSm,
    data,
    expandedSection,
    tabIndex,
    isLoanDetailsLoading,
    stableCoApplicantDetails,
    coApplicantCompletionRate,
  ]);

  // for progress calculation (any changes here, then make sure to check name based conditions)
  const applicationSteps = [
    { horizontalStepName: "Eligibility Check", verticalStepsCount: 3 },
    { horizontalStepName: "Applicant & Loan Details", verticalStepsCount: 6 },
    { horizontalStepName: "Provisional Offer Letter", verticalStepsCount: 0 },
    { horizontalStepName: "Co-Applicant", verticalStepsCount: 0 },
    {
      horizontalStepName: "Additional Details & Documents",
      verticalStepsCount: 4,
    },
    // {
    //   horizontalStepName: "Final Verification & Sanction",
    //   verticalStepsCount: 4,
    // },
    // { horizontalStepName: "Disbursement of Funds", verticalStepsCount: 8 },
  ];

  /** Aligns summary "Edit" with `stepper.horizontalSteps` / vertical steps (handles hidden Provisional step, etc.). */
  const resolveHorizontalIndexForSummarySection = (
    sectionLabel: string | undefined,
    summarySectionIndex: number
  ) => {
    const normalizedSectionLabel = normalizeSummarySectionLabel(sectionLabel);
    let hIdx = horizontalSteps.findIndex(
      (h: any) =>
        normalizeSummarySectionLabel(
          h?.horizontalStepName || h?.stepLabel || h?.stepKey
        ) === normalizedSectionLabel
    );
    if (hIdx === -1) {
      const filteredSteps =
        fetchUserApplicationData?.partnerStartedJourney === true
          ? applicationSteps.filter(
              (s) =>
                s.horizontalStepName.toLowerCase() !==
                "provisional offer letter"
          )
          : applicationSteps;
      const fromFiltered = filteredSteps.findIndex(
        (s) =>
          normalizeSummarySectionLabel(s.horizontalStepName) ===
          normalizedSectionLabel
      );
      hIdx = fromFiltered !== -1 ? fromFiltered : summarySectionIndex;
    }
    return hIdx;
  };

  const resolveVerticalIndexForSummaryRow = (
    hIdx: number,
    verticalStep: VerticalStep,
    verticalLoopIndex: number
  ) => {
    const vs = horizontalSteps[hIdx]?.verticalSteps;
    if (vs?.length) {
      const normalizedVerticalName = normalizeSummaryVerticalStepName(
        verticalStep.verticalStepName
      );
      const isAdditionalDetailsSection =
        normalizeSummarySectionLabel(
          horizontalSteps[hIdx]?.horizontalStepName ||
            horizontalSteps[hIdx]?.stepLabel ||
            horizontalSteps[hIdx]?.stepKey
        ) === "Additional Details & Documents";

      const searchableSteps = isAdditionalDetailsSection
        ? vs.filter((v: any) => !isHiddenAdditionalDetailsVerticalStep(v?.verticalStepName || v?.stepLabel))
        : vs;

      const byName = searchableSteps.findIndex(
        (v: any) =>
          normalizeSummaryVerticalStepName(
            v?.verticalStepName || v?.stepLabel
          ) === normalizedVerticalName
      );
      if (byName !== -1) {
        if (!isAdditionalDetailsSection) {
          return byName;
        }
        let rawIndex = -1;
        let visibleIndex = -1;
        for (let i = 0; i < vs.length; i++) {
          if (isHiddenAdditionalDetailsVerticalStep(vs[i]?.verticalStepName || vs[i]?.stepLabel)) {
            continue;
          }
          visibleIndex += 1;
          if (visibleIndex === byName) {
            rawIndex = i;
            break;
          }
        }
        return rawIndex !== -1 ? rawIndex : byName;
      }
    }
    return verticalLoopIndex;
  };

  const syncStepperIndicesFromSummarySection = (
    sectionLabel: string | undefined,
    summarySectionIndex: number,
    verticalStep: VerticalStep,
    verticalLoopIndex: number
  ) => {
    const hIdx = resolveHorizontalIndexForSummarySection(
      sectionLabel,
      summarySectionIndex
    );
    const vIdx = resolveVerticalIndexForSummaryRow(
      hIdx,
      verticalStep,
      verticalLoopIndex
    );
    setSummaryEditTarget({ h: hIdx, v: vIdx, name: verticalStep.verticalStepName });
    dispatch(setSummaryEditOpenedFromCoApplicantSection(false));
    if (verticalStep.verticalStepName === COLLATERAL_DETAILS_STEP_NAME) {
      dispatch(setSummaryReferenceCollateralEditTab(1));
    } else if (verticalStep.verticalStepName === REFERENCE_DETAILS_STEP_NAME) {
      dispatch(setSummaryReferenceCollateralEditTab(0));
    } else {
      dispatch(setSummaryReferenceCollateralEditTab(null));
    }
    dispatch(setActiveHorizontalIndex(hIdx));
    dispatch(setActiveVerticalIndex(vIdx));
    if (isApplicantLoanCoApplicantVerticalStep(verticalStep)) {
      dispatch(
        setLoanDetailCoApplicantCollapseValue(LOAN_DETAILS_SUMMARY_EDIT_FOCUS_KEY)
      );
    }
    dispatch(setSummaryApplicationEditActive(true));
  };

  const getActiveSummaryEditSectionName = () => {
    if (summaryEditOpenedFromCoApplicantSection) {
      const coApplicantDisplayIndex =
        activeInlineEditCoApplicantDisplayIndex ??
        getCoApplicantDisplayIndex(activeInlineEditCoApplicantNumber);
      return coApplicantDisplayIndex
        ? `Co-Applicant ${coApplicantDisplayIndex}`
        : "Co-Applicant";
    }
    if (summaryEditTarget?.name) return summaryEditTarget.name;
    const activeV = activeVerticalIndexMap[activeHorizontalIndex] ?? 0;
    return (
      horizontalSteps?.[activeHorizontalIndex]?.verticalSteps?.[activeV]
        ?.verticalStepName || "another section"
    );
  };

  const closeSummaryEditSwitchAlert = () => {
    pendingSummaryEditActionRef.current = null;
    setSummaryEditSwitchAlert(null);
  };

  const confirmSummaryEditSwitchAlert = () => {
    const pendingAction = pendingSummaryEditActionRef.current;
    pendingSummaryEditActionRef.current = null;
    setSummaryEditSwitchAlert(null);
    pendingAction?.();
  };

  const runOrConfirmSummaryEditSwitch = (
    nextSectionName: string,
    onConfirm: () => void
  ) => {
    if (!summaryApplicationEditActive) {
      onConfirm();
      return;
    }
    const currentSectionName = getActiveSummaryEditSectionName();
    if (currentSectionName === nextSectionName) {
      onConfirm();
      return;
    }
    pendingSummaryEditActionRef.current = onConfirm;
    setSummaryEditSwitchAlert({ currentSectionName, nextSectionName });
  };

  useEffect(() => {
    if (!summaryApplicationEditActive) {
      setSummaryEditTarget(null);
      setActiveInlineEditCoApplicantDisplayIndex(null);
    }
  }, [summaryApplicationEditActive]);

  const handleAddCoApplicantFromSummary = (
    event: React.MouseEvent<HTMLButtonElement>,
    coApplicantNumber: number,
    displayIndex?: number
  ) => {
    event.stopPropagation();
    const coApplicantDisplayIndex = displayIndex ?? coApplicantNumber;
    runOrConfirmSummaryEditSwitch(`Co-Applicant ${coApplicantDisplayIndex}`, () => {
      setSummaryEditTarget(null);
      setActiveInlineEditCoApplicantDisplayIndex(coApplicantDisplayIndex);
      dispatch(setDisableAddMoreCoApplicant(true));
      dispatch(setCoApplicantSummaryViewOnly(false));
      dispatch(setSummaryEditOpenedFromCoApplicantSection(true));
      dispatch(setActiveHorizontalIndex(1));
      dispatch(setActiveVerticalIndex(5));
      dispatch(
        setLoanDetailCoApplicantCollapseValue(`CoApplicant${coApplicantNumber}`)
      );
      dispatch(setSummaryApplicationEditActive(true));
    });
  };

  const handleCoApplicantRemovedFromSummary = () => {
    setSummaryEditTarget(null);
    dispatch(setLoanDetailCoApplicantCollapseValue(""));
    dispatch(setCoApplicantSummaryViewOnly(false));
    dispatch(setSummaryEditOpenedFromCoApplicantSection(false));
    dispatch(setSummaryApplicationEditActive(false));
    dispatch(setDisableAddMoreCoApplicant(false));
    fetchSummaryData();
  };

  const handleCancelSummaryEdit = () => {
    setSummaryEditTarget(null);
    dispatch(setSummaryFormSaveInProgress(false));
    dispatch(setLoanDetailCoApplicantCollapseValue(""));
    dispatch(setCoApplicantSummaryViewOnly(false));
    dispatch(setSummaryEditOpenedFromCoApplicantSection(false));
    dispatch(setSummaryReferenceCollateralEditTab(null));
    dispatch(setSummaryApplicationEditActive(false));
  };

  const handleEditCoApplicantFromSummary = (
    _summarySectionIndex: number,
    coApplicantNumber: number,
    displayIndex?: number
  ) => {
    // For SUMMARY edit, always open Co-Applicant Details section.
    const coApplicantDisplayIndex = displayIndex ?? coApplicantNumber;
    runOrConfirmSummaryEditSwitch(`Co-Applicant ${coApplicantDisplayIndex}`, () => {
      setSummaryEditTarget(null);
      setActiveInlineEditCoApplicantDisplayIndex(coApplicantDisplayIndex);
      dispatch(setCoApplicantSummaryViewOnly(false));
      dispatch(setSummaryEditOpenedFromCoApplicantSection(true));
      dispatch(setActiveHorizontalIndex(1));
      dispatch(setActiveVerticalIndex(5));
      dispatch(
        setLoanDetailCoApplicantCollapseValue(`CoApplicant${coApplicantNumber}`)
      );
      dispatch(setSummaryApplicationEditActive(true));
    });
  };

  function getCombinedApplicationSteps(
    selectedApp: Application,
    applicationSteps: {
      horizontalStepName: string;
      verticalStepsCount: number;
    }[]
  ): CombinedStep[] {
    const combinedSteps: CombinedStep[] = [];
    const hardcodedApplicantLoanTabs: VerticalStep[] = [
      { verticalStepName: "Student Details", fields: [] },
      { verticalStepName: "Address Details", fields: [] },
      { verticalStepName: "Employment Details", fields: [] },
      { verticalStepName: "Academic Details", fields: [] },
      { verticalStepName: "Entrance Exam Details", fields: [] },
      { verticalStepName: "Co-Applicant Details", fields: [] },
    ];

    const isSelectedPartnerJourney =
      (selectedApp as any)?.partnerStartedJourney === true;
    const filteredSteps = applicationSteps;

    const staticStepsMap = new Map(
      filteredSteps?.map((step) => [
        step.horizontalStepName,
        step.verticalStepsCount,
      ])
    );

    // Set isEligible based on isEligibilityChecked for initial steps
    const getInitialIsEligible = (stepName: string) => {
      if (stepName === "Eligibility Check") {
        return selectedApp?.isEligibilityChecked === false ? false : 
               selectedApp?.isEligibilityChecked === null ? null : true;
      }
      return true; // Other steps default to true
    };

    for (const step of filteredSteps ?? []) {
      combinedSteps.push({
        label: step.horizontalStepName,
        completionRate: "0%",
        status:
          step.horizontalStepName === "Co-Applicant" &&
          coApplicantCompletionRate === 100
            ? "completed"
            : "pending",
        innerData: [],
        isEligible: getInitialIsEligible(step.horizontalStepName),
      });
    }
    if (selectedApp && selectedApp.horizontalSteps) {
      for (const dynamicStep of selectedApp.horizontalSteps) {
        const stepLabel = dynamicStep?.horizontalStepName;
        const staticVerticalStepsCount = staticStepsMap.get(stepLabel);

        if (staticVerticalStepsCount !== undefined) {
          const completionPercentage: any = dynamicStep?.completionRate ?? 0;

          const stepStatus =
            completionPercentage === 100 ? "completed" : "pending";
          const stepIndex = combinedSteps.findIndex(
            (step) => step?.label === stepLabel
          );

          if (stepIndex !== -1) {
            const shouldUseHardcodedApplicantLoanTabs =
              isSelectedPartnerJourney &&
              stepLabel === "Applicant & Loan Details";

            combinedSteps[stepIndex] = {
              label: stepLabel,
              completionRate: `${completionPercentage}%`,
              status: stepStatus,
              innerData: shouldUseHardcodedApplicantLoanTabs
                ? hardcodedApplicantLoanTabs
                : dynamicStep?.verticalSteps || [],
              isEligible: combinedSteps[stepIndex].isEligible, // Preserve isEligible
            };
          }
        }
      }
    }

    // Partner Journey Logic: If partner journey has begun, mark first 4 steps as completed and Final Verification as in-progress
    if (isSelectedPartnerJourney) {
      const eligibilityIndex = combinedSteps.findIndex(
        (step) => step?.label === "Eligibility Check"
      );
      const applicantDetailsIndex = combinedSteps.findIndex(
        (step) => step?.label === "Applicant & Loan Details"
      );
      const provisionalOfferIndex = combinedSteps.findIndex(
        (step) => step?.label === "Provisional Offer Letter"
      );
      const additionalDetailsIndex = combinedSteps.findIndex(
        (step) => step?.label === "Additional Details & Documents"
      );
      const finalVerificationIndex = combinedSteps.findIndex(
        (step) => step?.label === "Final Verification & Sanction"
      );

      // Mark first 4 steps as completed (ticked)
      const stepsToComplete = [
        eligibilityIndex,
        applicantDetailsIndex,
        provisionalOfferIndex,
        additionalDetailsIndex
      ];

      stepsToComplete.forEach((stepIndex) => {
        if (stepIndex !== -1) {
          combinedSteps[stepIndex] = {
            ...combinedSteps[stepIndex],
            completionRate: "100%",
            status: "completed",
          };
        }
      });

      // Mark Final Verification & Sanction as in-progress
      if (finalVerificationIndex !== -1) {
        combinedSteps[finalVerificationIndex] = {
          ...combinedSteps[finalVerificationIndex],
          completionRate: "50%", // In-progress state
          status: "pending",
        };
      }
    }

    // isQualified Logic: If isQualified is true, mark Eligibility as complete and Applicant Details as in-progress
    if (selectedApp?.isQualified === true) {
      const eligibilityIndex = combinedSteps.findIndex(
        (step) => step?.label === "Eligibility Check"
      );
      
      const applicantDetailsIndex = combinedSteps.findIndex(
        (step) => step?.label === "Applicant & Loan Details"
      );

      // Mark Eligibility Check as completed (but only if not null)
      if (eligibilityIndex !== -1 && selectedApp?.isEligibilityChecked !== null) {
        combinedSteps[eligibilityIndex] = {
          ...combinedSteps[eligibilityIndex],
          completionRate: "100%",
          status: "completed",
          isEligible: true,
        };
      }

      // Mark Applicant Details as in-progress (only if not already completed)
      if (applicantDetailsIndex !== -1 && combinedSteps[applicantDetailsIndex].completionRate !== "100%") {
        combinedSteps[applicantDetailsIndex] = {
          ...combinedSteps[applicantDetailsIndex],
          status: "pending",
        };
      }
    }

    const eligibilityCheckStep = combinedSteps.find(
      (step) => step?.label === "Eligibility Check"
    );
    const applicantDetailsStep = combinedSteps.find(
      (step) => step?.label === "Applicant & Loan Details"
    );

    const areFirstTwoStepsCompleted =
      eligibilityCheckStep?.completionRate === "100%" &&
      applicantDetailsStep?.completionRate === "100%";

    // Mark Provisional Offer as complete based ONLY on isProvisionalGenerated flag
    if (selectedApp?.isProvisionalGenerated === true) {
      const provisionalOfferStepIndex = combinedSteps.findIndex(
        (step) => step?.label === "Provisional Offer Letter"
      );

      if (provisionalOfferStepIndex !== -1) {
        combinedSteps[provisionalOfferStepIndex] = {
          label: "Provisional Offer Letter",
          completionRate: "100%",
          status: "completed",
          innerData: [],
          isEligible: true,
        };
      }

      // Mark Additional Details as in-progress if not already completed
      const additionalDetailsIndex = combinedSteps.findIndex(
        (step) => step?.label === "Additional Details & Documents"
      );

      if (additionalDetailsIndex !== -1 && combinedSteps[additionalDetailsIndex].completionRate !== "100%") {
        combinedSteps[additionalDetailsIndex] = {
          ...combinedSteps[additionalDetailsIndex],
          status: "pending",
        };
      }
    }

    return combinedSteps;
  }

  const ProvisionalOfferModalContent = () => {
    const conditions = [
      "The In-principle sanction is provisional in nature, based on the information shared by you and is subject to revision basis verification of documents and further assessment/review.",
      "The In-principle sanction letter is subject to revision basis the credit underwriting norms, policies of Avanse and upon change in any information provided by you in the application form.",
      "The ROI / Processing Fees is subject to revision in terms of the final sanction letter /loan agreement basis verification of your profile / security or otherwise.",
      "The final sanction may be issued to you upon satisfactory (i) credit assessment (ii) KYC requirement (ii) Clearance of due diligence process (iii) payment of processing fees and (iv) your meeting the eligibility criteria set by Avanse Financial Services Ltd., under its credit policy.",
      "This In-principle sanction will automatically expire within 6 days from the date hereof and does not create any binding obligations on Avanse Financial Services Ltd., to sanction the loan and the same may be revoked or cancelled as per Avanse’s discretion.",
    ];

    const getRepaymentLabel = (type: string) => {
      let val= typeof type === "string"  ? type?.toUpperCase():type;
      switch (val) {
        case "PINT":
          return "Partial interest";
        case "SINT":
          return "Simple interest";
        case "EMI":
          return "EMI";
        default:
          return type || "-";
      }
    };

    return (
      <Box sx={{ background: "#fff", textAlign: "center" }}>
        {breData?.Provisional_Offer?.toLowerCase() == "yes" ? (
          <>
            {isXsOrSm ? (
              <Box>
                <img
                  src={successProvisionalOffer}
                  alt="Provisional Letter Icon"
                  style={{
                    width: "80px",
                    height: "90px",
                    marginBottom: 16,
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: "#000",
                    mb: 1,
                    fontSize: "22px",
                  }}
                >
                  <span style={{ color: "#00B3B5" }}>Congratulations, </span>
                  {userProfile?.name}!
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-end",
                  gap: 5,
                }}
              >
                <img
                  src={leftConfetti}
                  alt="Confetti Left"
                  style={{ objectFit: "contain" }}
                />
                <Box>
                  <img
                    src={successProvisionalOffer}
                    alt="Provisional Letter Icon"
                    style={{
                      width: "80px",
                      height: "90px",
                      marginBottom: 16,
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      color: "#000",
                      mb: 1,
                      fontSize: "22px",
                    }}
                  >
                    <span style={{ color: "#00B3B5" }}>Congratulations, </span>
                    {userProfile?.name}!
                  </Typography>
                </Box>
                <img
                  src={rightConfetti}
                  alt="Confetti Right"
                  style={{ objectFit: "contain" }}
                />
              </Box>
            )}

            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#555",
                mb: 0.5,
              }}
            >
              You've received a Provisional Loan Offer from us!
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#555",
                px: isXsOrSm ? 0 : 4,
              }}
            >
              Based on the information you've provided, we're excited to share
              this offer designed to support your academic journey.
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#555",
                px: isXsOrSm ? 0 : 4,
              }}
            >
              To move forward, just complete your application.
              {/* <span
                style={{
                  fontSize: "13px",
                  fontWeight: "800",
                  color: "#cb9a0a",
                }}
              >
                and you'll receive your Sanction Letter within 48 hours.
              </span> */}
            </Typography>

            <Box
              sx={{
                p: 2,
                my: 2,
                borderRadius: "8px",
                background: "#f8f9fa",
                border: "1px solid #e9ecef",
              }}
            >
              <Typography
                sx={{
                  mb: 1,
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#495057",
                  fontFamily: "Jost, sans-serif",
                  lineHeight: "1.4",
                  textAlign: "left",
                }}
              >
                A quick glance at your loan offer details:
              </Typography>

              {isXsOrSm ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {[
                    { label: "Max Loan Amount:", 
                      value: provisionalOffer?.Max_Loan_Amount 
                      ? `₹ ${new Intl.NumberFormat('en-IN').format(Number(provisionalOffer.Max_Loan_Amount))}` 
                      : "₹ 0"
                    },
                    { label: "Currency:", value: provisionalOffer?.currency || "Rupees (₹)" },
                    { label: "Max Tenure:", value: provisionalOffer?.Max_Tenure || "0 Years" },
                    { label: "Repayment Mode:", value: getRepaymentLabel(provisionalOffer?.Repayment_Type) },
                  ].map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#6c757d",
                          fontFamily: "Jost, sans-serif",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#495057",
                          fontFamily: "Jost, sans-serif",
                          textAlign: "right",
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "50%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#6c757d",
                          fontFamily: "Jost, sans-serif",
                        }}
                      >
                        Max Loan Amount:{" "}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#495057",
                          fontFamily: "Jost, sans-serif",
                        }}
                      >
                        {provisionalOffer?.Max_Loan_Amount 
                          ? `₹ ${new Intl.NumberFormat('en-IN').format(Number(provisionalOffer.Max_Loan_Amount))}` 
                          : "₹ 0"
                        }
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "50%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#6c757d",
                          fontFamily: "Jost, sans-serif",
                        }}
                      >
                        Currency:{" "}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#495057",
                          fontFamily: "Jost, sans-serif",
                        }}
                      >
                        {provisionalOffer?.currency || "Rupees (₹)"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      pt: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "50%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#6c757d",
                          fontFamily: "Jost, sans-serif",
                        }}
                      >
                        Max Tenure:{" "}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#495057",
                          fontFamily: "Jost, sans-serif",
                        }}
                      >
                        {provisionalOffer?.Max_Tenure || "0 Years"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "50%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#6c757d",
                          fontFamily: "Jost, sans-serif",
                        }}
                      >
                        Repayment Mode:{" "}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#495057",
                          fontFamily: "Jost, sans-serif",
                          textTransform: 'capitalize'
                        }}
                      >
                        {getRepaymentLabel(provisionalOffer?.Repayment_Type) || "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            <Typography className="text5lable" sx={{ textAlign: "left" }}>
              Terms & Conditions
            </Typography>
            <List dense sx={{ listStyleType: "decimal", pl: 2 }}>
              {conditions.map((condition: string, index: number) => (
                <ListItem
                  key={index}
                  className="inner-form-heading3"
                  sx={{
                    display: "list-item",
                    alignItems: "flex-start",
                    mb: "0px !important",
                    py: "0px !important",
                  }}
                >
                  <ListItemText primary={condition} />
                </ListItem>
              ))}
            </List>
          </>
        ) : (
          <>
            {/* <Box
              component="img"
              src={failedProvisionalOffer}
              alt="Offer Status"
              sx={{ width: "80px", height: "90px", mb: 2 }}
            />
            <Typography
              sx={{
                fontWeight: "600",
                color: "#FF4C51",
                mb: 2,
                fontSize: "20px",
              }}
            >
              Thank you for completing the details. While we cannot provide an instant offer right now, your application is still being considered. 
              A Sales Manager will review your profile and contact you shortly to discuss available financing options.
            </Typography> */}
        
            <Typography
              sx={{
                mt: { xs: 0.5, sm: 1 },
                mb: { xs: 0.75, sm: 1.50 },
                fontSize: "26px",
                fontWeight: 600,
              }}
            >
              You're One Step Closer
            </Typography>
            {/* Congratulations Text */}
            <Typography
              sx={{
                mt: { xs: 0.5, sm: 1 },
                fontSize: "18px",
                fontWeight: 400,
              }}
            >
              To personalize your loan offer, we require a few additional details from you. Please proceed to complete the remaining sections of your application.
            </Typography>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 400,
              }}
            >
              Our Sales Manager will review your profile and contact you shortly to discuss the available financing options.
            </Typography>
          </>
        )}

        <Button
          variant="contained"
          sx={{
            backgroundColor: "#00b3b5",
            color: "#fff",
            "&:hover": { backgroundColor: "#00b3b5" },
            fontWeight: "600",
            fontSize: "13px",
            px: 2.5,
            py: 1,
            borderRadius: 2,
            textTransform: "capitalize",
            mt: 2,
          }}
          onClick={() => setIsViewProvisionalOfferModalOpen(false)}
        >
          Close
        </Button>
      </Box>
    );
  };

  const PendingProvisionalOfferModalContent = () => {
    const handleOnContinuePendingProvision = () => {
      setOpenPendingModal(false);
    }

    return (
      <Box sx={{ background: "#fff", textAlign: "center" }}>
        <>
          <Box
            component="img"
            src={pendingProvisionalOffer}
            alt="Offer Status"
            sx={{ width: "80px", height: "90px", mb: 2 }}
          />
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#000000",
              mb: 2,
            }}
          >
            Provisional Offer Pending
          </Typography>
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: "400",
              color: "#00000080",
              mb: 2,
            }}
          >
            Please resume your customer journey to get best offers.
          </Typography>
        </>

        <Button
          variant="contained"
          sx={{
            backgroundColor: "#00b3b5",
            color: "#fff",
            "&:hover": { backgroundColor: "#00b3b5" },
            fontWeight: "600",
            fontSize: "13px",
            px: 2.5,
            py: 1,
            borderRadius: 2,
            textTransform: "capitalize",
            mt: 1,
          }}
          onClick={handleOnContinuePendingProvision}
        >
          Close
        </Button>
      </Box>
    );
  };

  const fetchSummaryData = useCallback(async () => {
    if (!token || !applicationNumber) return;
    const requestId = summaryDataRequestRef.current + 1;
    summaryDataRequestRef.current = requestId;
    const isLatestRequest = () => requestId === summaryDataRequestRef.current;
    setIsLoading(true);
    try {
      const [res]: any = await Promise.all([
        fetchUserApplicationsFormData(token, applicationNumber),
        dispatch(
          getFormJson({
            trackingId: applicationNumber,
            loaderShow: false,
          })
        ),
      ]);
      if (!isLatestRequest()) return;

        if (res?.status === 200 && res?.data?.applications?.length > 0) {
          const applicationRecord = res.data.applications[0];
          const shouldUsePartnerDataView = dataView
            ? dataView === "processio_data_view"
            : true;
          const effectivePartnerStartedJourney = Boolean(
            applicationRecord?.partnerStartedJourney === true &&
              shouldUsePartnerDataView
          );
          const effectiveApplicationRecord =
            applicationRecord?.partnerStartedJourney ===
            effectivePartnerStartedJourney
              ? applicationRecord
              : {
                  ...applicationRecord,
                  partnerStartedJourney: effectivePartnerStartedJourney,
                };
          let partnerExpenseDetails: any[] = [];
          let partnerReferenceDetails: any[] = [];
          let partnerCollateralDetails: any[] = [];
          applicantLoanTabDetailsFetchedRef.current = "";
          dispatch(setFetchUserApplicationData(effectiveApplicationRecord));
          if (!effectivePartnerStartedJourney) {
            dispatch(setApplicantLoanDetails({ StudentDetails: [], AddressDetails: {} }));
            dispatch(
              setEmploymentDetails({
                EmploymentDetails: [],
                CompanyAddressDetails: [],
              })
            );
            dispatch(setAcademicDetails({ academicDetails: [] }));
            dispatch(setEntranceDetails([]));
          }
          if (effectivePartnerStartedJourney) {
            try {
              const loanResp =
                await applicationDetailsServices.getLoanDetailsFromProcessio(
                  applicationNumber
                );
              if (!isLatestRequest()) return;
              latestProcessioLoanOverride = loanResp?.data?.data ?? null;
            } catch (loanError) {
              console.log("getLoanDetailsFromProcessio failed", loanError);
              if (!isLatestRequest()) return;
              latestProcessioLoanOverride = null;
            }
            const partnerDetailsKey = `${applicationRecord?.applicationId || ""}:${applicationNumber}`;
            if (partnerAdditionalDetailsRef.current.key !== partnerDetailsKey) {
              partnerAdditionalDetailsRef.current = {
                key: partnerDetailsKey,
                data: null,
                promise: Promise.allSettled([
                  applicationDetailsServices.getExpenseDetails(
                    String(applicationRecord?.applicationId || ""),
                    applicationNumber
                  ),
                  applicationDetailsServices.getReferenceDetails(
                    String(applicationRecord?.applicationId || ""),
                    applicationNumber
                  ),
                  applicationDetailsServices.getCollateralDetails(
                    String(applicationRecord?.applicationId || ""),
                    applicationNumber
                  ),
                ]).then(([expenseResp, referenceResp, collateralResp]: any) => ({
                  expense: expenseResp?.status === "fulfilled"
                    ? getPartnerDetailsArray(expenseResp?.value?.data?.data, [
                        "ExpenseDetails",
                        "ExpenseFundsDetails",
                        "FundsDetails",
                      ])
                    : [],
                  reference: referenceResp?.status === "fulfilled"
                    ? getPartnerDetailsArray(referenceResp?.value?.data?.data, [
                        "ReferenceDetails",
                      ])
                    : [],
                  collateral: collateralResp?.status === "fulfilled"
                    ? getPartnerDetailsArray(collateralResp?.value?.data?.data, [
                        "CollateralDetails",
                      ])
                    : [],
                })),
              };
            }
            const partnerAdditionalDetails =
              partnerAdditionalDetailsRef.current.data ||
              await partnerAdditionalDetailsRef.current.promise;
            if (!isLatestRequest()) return;
            partnerAdditionalDetailsRef.current.data = partnerAdditionalDetails;
            partnerExpenseDetails = partnerAdditionalDetails.expense;
            partnerReferenceDetails = partnerAdditionalDetails.reference;
            partnerCollateralDetails = partnerAdditionalDetails.collateral;
        } else {
          latestProcessioLoanOverride = null;
        }
        const { expenseDetails, referenceDetails, collateralDetails } =
          extractAdditionalDetailsFromApplication(applicationRecord);

        dispatch(setExpenseDetails(expenseDetails ?? []));
        dispatch(setReferenceDetails(referenceDetails ?? []));
        dispatch(setCollateralDetails(collateralDetails ?? []));
        hydrateAdditionalDetailsReduxFromHorizontalSteps(
          applicationRecord,
          dispatch
        );
        if (effectivePartnerStartedJourney) {
          dispatch(setExpenseDetails(partnerExpenseDetails));
          dispatch(setReferenceDetails(partnerReferenceDetails));
          dispatch(setCollateralDetails(partnerCollateralDetails));
        }

        const rawSteps = getCombinedApplicationSteps(
          effectiveApplicationRecord,
          applicationSteps
        );

        setData(rawSteps || []);
      }
    } catch (e) {
      console.log(e);
    } finally {
      if (isLatestRequest()) {
        setIsLoading(false);
      }
    }
  }, [
    applicationNumber,
    token,
    formJsonRefreshNonce,
    dispatch,
    dataView,
    actualPartnerStartedJourney,
  ]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  useEffect(() => {
    const breObj = breData;
    const isBlankObject =
      breObj &&
      Object.keys(breObj).length > 0 &&
      Object.values(breObj).every(
        (v) => v === "" || v === null || v === undefined
      );
    if (isBlankObject === undefined) {
      setIsBreDataFlag(false);
    } else {
      setIsBreDataFlag(!isBlankObject);
    }
  }, [breData, applicationNumber]);

  return (
    <Box>
      {isLoading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5]?.map((item) => (
            <Grid item xs={12} key={item}>
              <Card
                sx={{
                  borderRadius: "10px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  p: 2,
                  "&:hover": {
                    boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
                    transform: "translateY(-2px)",
                    transition: "all 0.3s ease-in-out",
                  },
                }}
              >
                <CardContent sx={{ p: "16px !important" }}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item>
                      <Skeleton
                        variant="rectangular"
                        width={35}
                        height={35}
                        sx={{ borderRadius: "8px" }}
                      />
                    </Grid>
                    <Grid item xs>
                      <Skeleton
                        variant="text"
                        width="60%"
                        height={20}
                        sx={{ mb: 1 }}
                      />
                      <Skeleton variant="text" width="80%" height={16} />
                    </Grid>
                    <Grid item>
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{ minWidth: "100px", justifyContent: "flex-end" }}
                      >
                        <Skeleton
                          variant="text"
                          width={60}
                          height={25}
                          sx={{ mr: 1 }}
                        />
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton
                          variant="circular"
                          width={24}
                          height={24}
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box>
          {data &&
            data?.map((application, appIndex) => {
              const IconComponent =
                memoizedIcons[application?.label] || DocumentIcon;

              const iconProps =
                IconComponent === CustomImageIcon
                  ? { src: iconSrcMap[application?.label] }
                  : {};

              const defaultApplicantLoanDetailsTabs: VerticalStep[] = [
                { verticalStepName: "Student Details", fields: [] },
                { verticalStepName: "Address Details", fields: [] },
                { verticalStepName: "Employment Details", fields: [] },
                { verticalStepName: "Academic Details", fields: [] },
                { verticalStepName: "Entrance Exam Details", fields: [] },
                { verticalStepName: "Co-Applicant Details", fields: [] },
              ];
              const mergeWithDefaultTabs = (
                sourceTabs: VerticalStep[],
                defaultTabs: VerticalStep[]
              ): VerticalStep[] => {
                const sourceByName = new Map(
                  (sourceTabs || []).map((tab) => [tab?.verticalStepName, tab])
                );
                return defaultTabs.map(
                  (defaultTab) =>
                    sourceByName.get(defaultTab.verticalStepName) ?? defaultTab
                );
              };

              const originalInnerData: VerticalStep[] = application?.innerData || [];
              let innerDataToUse: VerticalStep[] = originalInnerData;
              if (application?.label === "Applicant & Loan Details") {
                innerDataToUse = mergeWithDefaultTabs(
                  innerDataToUse,
                  defaultApplicantLoanDetailsTabs
                ).filter(
                  (tab) => !isHiddenApplicantLoanVerticalStep(tab?.verticalStepName)
                );
              } else if (
                application?.label === "Additional Details & Documents"
              ) {
                innerDataToUse = mergeAdditionalDetailsTabs(innerDataToUse);
              }

              const isCoApplicantSection =
                application?.label === "Co-Applicant";
              const shouldShowCoApplicantTabs =
                isCoApplicantSection && innerDataToUse.length === 0;
              const shouldRenderStackedInnerSections =
                application?.label === "Eligibility Check" ||
                application?.label === "Applicant & Loan Details" || application?.label === "Additional Details & Documents";
              const eligibilitySectionActionMap: Record<
                string,
                { label: string; clickable: boolean }
              > = {
                "Verify Yourself and Provide Consent": {
                  label: "Edit",
                  clickable: true,
                },
                "Provide Course Details": { label: "Edit", clickable: true },
                "Provide Loan Details": { label: "Edit", clickable: true },
                Eligibility: { label: "View", clickable: true },
                "Student Details": { label: "Edit", clickable: true },
                "Address Details": { label: "Edit", clickable: true },
                "Employment Details": { label: "Edit", clickable: true },
                "Academic Details": { label: "Edit", clickable: true },
                "Entrance Exam Details": { label: "Edit", clickable: true },
                "Loan Details": { label: "Edit", clickable: true },
                "Co-Applicant Details": { label: "Edit", clickable: true },
                "Applicant Additional Details": { label: "Edit", clickable: true },
                "Funds & Expenses": { label: "Edit", clickable: true },
                "Reference & Collateral Details": { label: "Edit", clickable: true },
                "Reference Details": { label: "Edit", clickable: true },
                "Collateral Details": { label: "Edit", clickable: true },
              };
              const isSubmittedApplication =
                ["application submitted", "submitted"].includes(
                  String(fetchUserApplicationData?.status || applicationStatus || "").toLowerCase()
                );
              const hideAllSummaryEdits =
                !!actualPartnerStartedJourney || isSubmittedApplication;
              
              const applicantSummaryIndex = data.findIndex(
                (s) => s?.label === "Applicant & Loan Details"
              );
              const applicantLoanSectionHIdx =
                resolveHorizontalIndexForSummarySection(
                  "Applicant & Loan Details",
                  applicantSummaryIndex >= 0 ? applicantSummaryIndex : appIndex
                );
              const activeVForStepper =
                activeVerticalIndexMap[activeHorizontalIndex] ?? 0;
              const activeVerticalStepNameForEdit =
                horizontalSteps[activeHorizontalIndex]?.verticalSteps?.[
                  activeVForStepper
                ]?.verticalStepName ?? "";
              const hideCoApplicantWhileEditingApplicantLoanDetails =
                isCoApplicantSection &&
                summaryApplicationEditActive &&
                !summaryEditOpenedFromCoApplicantSection &&
                activeHorizontalIndex === applicantLoanSectionHIdx &&
                ["Loan Details", "Co-Applicant Details"].includes(
                  activeVerticalStepNameForEdit
                );

              if (hideCoApplicantWhileEditingApplicantLoanDetails) {
                return null;
              }

              return (
                <CollapsibleSection
                  key={application?.label}
                  icon={() => <IconComponent {...iconProps} />}
                  title={application?.label}
                  subtitle={`${application?.label} Related Details`}
                  completionRate={
                    application?.label === "Co-Applicant"
                      ? `${
                          coApplicantCompletionRate
                            ? coApplicantCompletionRate
                            : 0
                        }%`
                      : String(application?.completionRate)
                  }
                  isCompleted={application?.status === "completed"}
                  isExpanded={expandedSection === appIndex && !isXsOrSm}
                  onToggle={() => onToggleHandler(application, appIndex)}
                  isProvisionalOfferTab={application?.label.toLowerCase() === "provisional offer letter"}
                  provisionalOfferBtnClick={() =>
                    application?.completionRate === "0%" || !userProfile?.name
                      ? setOpenPendingModal(true)
                      : setIsViewProvisionalOfferModalOpen(true)
                  }
                  isProvisionalOffer={isBreDataFlag}
                  partnerStartedJourney={actualPartnerStartedJourney}
                >
                  <Box>
                    {shouldShowCoApplicantTabs ? (
                      (() => {
                        const coApplicantHorizontalIndex =
                          resolveHorizontalIndexForSummarySection(
                            "Co-Applicant",
                            appIndex
                          );
                        const activeCoApplicantVerticalIndex =
                          activeVerticalIndexMap?.[1] ?? 0;
                        const isEditingCoApplicantSection =
                          summaryApplicationEditActive &&
                          summaryEditOpenedFromCoApplicantSection &&
                          activeHorizontalIndex === 1 &&
                          activeCoApplicantVerticalIndex === 5;

                        if (isEditingCoApplicantSection) {
                          return !stableCoApplicantDetails ? (
                            referenceDetailsSkeleton()
                          ) : (
                            <>
                                <CoApplicantTabs
                                  coApplicant={stableCoApplicantDetails}
                                  pageType="SUMMARY"
                                  designationsVal={designations}
                                  renderInlineEditForm
                                  activeInlineEditCoApplicantNumber={
                                    activeInlineEditCoApplicantNumber
                                  }
                                  hideEditAction={hideAllSummaryEdits}
                                  editDisabled={false}
                                  onAddCoApplicant={
                                    handleAddCoApplicantFromSummary
                                  }
                                  disableAddMoreCoApplicant={
                                    disableAddMoreCoApplicant
                                  }
                                  onCoApplicantRemoved={
                                    handleCoApplicantRemovedFromSummary
                                  }
                                  onEditClick={(coApplicantNumber, displayIndex) =>
                                    handleEditCoApplicantFromSummary(
                                      appIndex,
                                      coApplicantNumber,
                                      displayIndex
                                    )
                                  }
                                />
                            </>
                          );
                        }

                        return !stableCoApplicantDetails ? (
                          referenceDetailsSkeleton()
                        ) : (
                          <CoApplicantTabs
                            coApplicant={stableCoApplicantDetails}
                            pageType="SUMMARY"
                            designationsVal={designations}
                            hideEditAction={hideAllSummaryEdits}
                            editDisabled={false}
                            onAddCoApplicant={handleAddCoApplicantFromSummary}
                            disableAddMoreCoApplicant={disableAddMoreCoApplicant}
                            onCoApplicantRemoved={handleCoApplicantRemovedFromSummary}
                            onEditClick={(coApplicantNumber, displayIndex) =>
                              handleEditCoApplicantFromSummary(
                                appIndex,
                                coApplicantNumber,
                                displayIndex
                              )
                            }
                          />
                        );
                      })()
                    ) : (
                      <>
                        {!shouldRenderStackedInnerSections && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-start",
                              alignItems: "center",
                            }}
                          >
                            <InnerDetailsTabs
                              innerData={innerDataToUse}
                              activeTabIndex={tabIndex}
                              activeHandleChange={handleChange}
                              isXsOrSm={isXsOrSm}
                            />
                          </Box>
                       
                        )}

                        {shouldRenderStackedInnerSections ? (
                          <Box sx={{ mt: 0.5 }}>
                            {innerDataToUse?.map(
                              (verticalStep: VerticalStep, i: number) => {
                                if (
                                  application?.label === "Applicant & Loan Details" &&
                                  isApplicantLoanCoApplicantVerticalStep(verticalStep)
                                ) {
                                  return null;
                                }
                                const sectionH =
                                  resolveHorizontalIndexForSummarySection(
                                    application?.label,
                                    appIndex
                                  );
                                const rowV = resolveVerticalIndexForSummaryRow(
                                  sectionH,
                                  verticalStep,
                                  i
                                );
                                const activeV =
                                  activeVerticalIndexMap[
                                    activeHorizontalIndex
                                  ] ?? 0;
                                const baseAction =
                                  eligibilitySectionActionMap[
                                    verticalStep.verticalStepName
                                  ];
                                const isApplicantLoanDetailsLoanRow =
                                  application?.label ===
                                    "Applicant & Loan Details" &&
	                                  isApplicantLoanCoApplicantVerticalStep(verticalStep);
                                const isAdditionalDetailsRow =
                                  application?.label ===
                                  "Additional Details & Documents";
                                const isSummaryEditTargetRow =
                                  summaryApplicationEditActive &&
                                  summaryEditTarget?.h === sectionH &&
                                  summaryEditTarget?.v === rowV &&
                                  summaryEditTarget?.name ===
                                    verticalStep.verticalStepName;
                                const shouldSuppressApplicantLoanRowEdit =
                                  isApplicantLoanDetailsLoanRow &&
                                  summaryEditOpenedFromCoApplicantSection &&
                                  activeHorizontalIndex === 1 &&
                                  activeV === 5;
                                const isActiveStepperRow =
                                  summaryApplicationEditActive &&
                                  activeHorizontalIndex === sectionH &&
                                  activeV === rowV;
                                const isEditableRowActive =
                                  isAdditionalDetailsRow
                                    ? isSummaryEditTargetRow
                                    : isSummaryEditTargetRow ||
                                      isActiveStepperRow;
                                const isEditingCurrentRow =
                                  isEditableRowActive &&
                                  baseAction?.label === "Edit" &&
                                  !hideAllSummaryEdits &&
                                  !shouldSuppressApplicantLoanRowEdit;
                                const rowActionLabel = isEditingCurrentRow
                                  ? "Save"
                                  : hideAllSummaryEdits
                                    ? ""
                                    : baseAction?.label || "";
                                const hideLabelValueRow =
                                  isEditableRowActive &&
                                  !shouldSuppressApplicantLoanRowEdit;
                                const globalRowIdxForNoDetails =
                                  application?.label === "Eligibility Check"
                                    ? i
                                    : application?.label ===
                                        "Applicant & Loan Details"
                                      ? stackedSequentialSummaryGate.eligStepCount +
                                        i
                                      : i;
                                const sequentialNoDetailsBlocked =
                                  stackedSequentialSummaryGate.firstIncompleteIdx >=
                                    0 &&
                                  globalRowIdxForNoDetails >
                                    stackedSequentialSummaryGate.firstIncompleteIdx;
                                const additionalDetailsEditBlocked =
                                  isAdditionalDetailsRow &&
                                  isAdditionalDetailsSectionEditBlocked(
                                    innerDataToUse,
                                    verticalStep.verticalStepName,
                                    fetchUserApplicationData?.horizontalSteps
                                  );
                                const provideLoanEditBlocked =
                                  application?.label === "Eligibility Check" &&
                                  verticalStep.verticalStepName ===
                                    "Provide Loan Details" &&
                                  !hasRequiredFieldsForSummaryGate(
                                    fetchUserApplicationData,
                                    1,
                                    2,
                                    [
                                      "courseCategory",
                                      "admissionStatus",
                                      "country",
                                      "courseType",
                                    ]
                                  );
                                const studentDetailsEditBlocked =
                                  application?.label ===
                                    "Applicant & Loan Details" &&
                                  verticalStep.verticalStepName ===
                                    "Student Details" &&
                                  !hasRequiredFieldsForSummaryGate(
                                    fetchUserApplicationData,
                                    1,
                                    3,
                                    ["totalCost", "loanAmount"],
                                    true
                                  );
                                const stackedRowEditDisabled =
                                  rowActionLabel === "Edit" &&
                                  (!baseAction?.clickable ||
                                    sequentialNoDetailsBlocked ||
                                    additionalDetailsEditBlocked ||
                                    provideLoanEditBlocked ||
                                    studentDetailsEditBlocked);
                                    console.log(verticalStep, 'verticalStep')
                                return (
                                <Box
                                  key={`${verticalStep.verticalStepName}-${i}`}
                                  sx={{
                                    border: "1px solid #cfe9ec",
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                    mb: 2,
                                    backgroundColor: "#ffffff",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: "60px",
                                      px: 2,
                                      py: 1.4,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      backgroundColor: "#eaf7f9",
                                      borderBottom: "1px solid #d6eef1",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontFamily: "Jost, sans-serif",
                                        fontSize: "14px",
                                        fontWeight: 700,
                                        lineHeight: "18px",
                                        letterSpacing: "0px",
                                        color: "#354052",
                                      }}
                                    >
                                      {verticalStep.verticalStepName}
                                    </Typography>
                                    {rowActionLabel && (
                                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, ml: "auto", flexShrink: 0 }}>
                                        <Button
                                          variant={rowActionLabel === "Save" ? "contained" : "text"}
                                          size="small"
                                          startIcon={
                                            rowActionLabel === "Edit" ? (
                                              <BorderColorOutlined
                                                sx={{
                                                  fontSize: "16px",
                                                  lineHeight: "16px",
                                                  verticalAlign: "middle",
                                                }}
                                              />
                                            ) : rowActionLabel === "View" ? (
                                              <VisibilityOutlined sx={{ fontSize: "16px" }} />
                                            ) : rowActionLabel === "Save" &&
                                              summaryFormSaveInProgress ? (
                                              <CircularProgress
                                                size={14}
                                                thickness={5}
                                                sx={{ color: "#00b3b5" }}
                                              />
                                            ) : undefined
                                          }
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            if (rowActionLabel === "Save") {
                                              dispatch(setSummaryFormSaveInProgress(true));
                                              dispatch(requestSummaryFormSave());
                                            } else if (baseAction?.label === "Edit") {
                                              runOrConfirmSummaryEditSwitch(
                                              verticalStep.verticalStepName,
                                              () => {
                                                setTabIndex(i);
                                                syncStepperIndicesFromSummarySection(
                                                  application?.label,
                                                  appIndex,
                                                  verticalStep,
                                                  i
                                                );
                                              }
                                            )
                                          }
                                          }}
                                          disabled={
                                            rowActionLabel === "Save"
                                              ? summaryFormSaveInProgress
                                              : rowActionLabel === "Edit"
                                              ? stackedRowEditDisabled
                                              : !baseAction?.clickable
                                          }
                                          title={undefined}
                                          sx={
                                            rowActionLabel === "Save"
                                              ? summaryActionButtonStyles.save
                                              : summaryActionButtonStyles.textAction
                                          }
                                        >
                                          {rowActionLabel}
                                        </Button>
                                        {rowActionLabel === "Save" && (
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              handleCancelSummaryEdit();
                                            }}
                                            disabled={summaryFormSaveInProgress}
                                            sx={summaryActionButtonStyles.cancel}
                                          >
                                            Cancel
                                          </Button>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                  {hideLabelValueRow ? (
                                    <Box
                                      sx={{
                                        px: { xs: 1, sm: 2 },
                                        py: 2,
                                        borderTop: "1px solid #d6eef1",
                                        backgroundColor: "#fafcfd",
                                      }}
                                    >
                                      <HorizontalLinearStepper
                                        status={true}
                                        formPageType="SUMMARY"
                                        module="ASSSITED"
                                      />
                                    </Box>
                                  ) : (
                                    <Box sx={{ px: 2, py: 2 }}>
                                      <RenderTabContent
                                        applicationLabel={application?.label}
                                        verticalStep={verticalStep}
                                        isMobile={false}
                                        isLoanDetailsLoading={
                                          isLoanDetailsLoading
                                        }
                                        designationsVal={designations}
                                        pageType="SUMMARY"
                                      />
                                    </Box>
                                  )}
                                </Box>
                                );
                              }
                            )}
                          </Box>
                        ) : (
                          !isXsOrSm && (
                          <Box>
                            {innerDataToUse?.map(
                              (verticalStep: VerticalStep, i: number) => (
                                <TabPanel value={tabIndex} index={i} key={i}>
                                  <RenderTabContent
                                    applicationLabel={application?.label}
                                    verticalStep={verticalStep}
                                    isMobile={false}
                                    isLoanDetailsLoading={isLoanDetailsLoading}
                                    designationsVal={designations}
                                    pageType="SUMMARY"
                                  />
                                </TabPanel>
                              )
                            )}
                          </Box>
                          )
                        )}
                      </>
                    )}
                  </Box>
                </CollapsibleSection>
              );
            })}

          <ModalPopup
            title="Unsaved Changes"
            isOpen={Boolean(summaryEditSwitchAlert)}
            onClose={closeSummaryEditSwitchAlert}
            style={{ maxWidth: "420px", width: "100%" }}
          >
            <Box sx={{ px: 1, pb: 1 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  lineHeight: "22px",
                  color: "#6f6b7d",
                  mb: 2,
                }}
              >
                {summaryEditSwitchAlert?.currentSectionName} is currently in
                edit mode. Switching to {summaryEditSwitchAlert?.nextSectionName} may
                discard unsaved changes.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1.5,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={closeSummaryEditSwitchAlert}
                  sx={{
                    minWidth: 100,
                    height: 38,
                    borderRadius: "4px",
                    textTransform: "none",
                    borderColor: "#00B3B5",
                    color: "#00B3B5",
                    "&:hover": {
                      borderColor: "#00B3B5",
                      backgroundColor: "rgba(0, 179, 181, 0.06)",
                    },
                  }}
                >
                  Stay Here
                </Button>
                <Button
                  variant="contained"
                  onClick={confirmSummaryEditSwitchAlert}
                  sx={{
                    minWidth: 100,
                    height: 38,
                    borderRadius: "4px",
                    textTransform: "none",
                    backgroundColor: "#00B3B5",
                    color: "#FFFFFF",
                    "&:hover": { backgroundColor: "#00999A" },
                  }}
                >
                  Continue
                </Button>
              </Box>
            </Box>
          </ModalPopup>

          <ModalPopup
            title="Provisional Offer Letter"
            isOpen={isOpenPendingModal}
            onClose={() => setOpenPendingModal(false)}
            style={{ width: "100%" }}
          >
            <PendingProvisionalOfferModalContent />
          </ModalPopup>

          <ModalPopup
            title="Provisional Offer Letter"
            isOpen={isViewProvisionalOfferModalOpen}
            onClose={() => setIsViewProvisionalOfferModalOpen(false)}
            style={
              breData?.Provisional_Offer?.toLowerCase() == "yes" ? { maxWidth: "800px", width: "100%" } : {}
            }
          >
            <ProvisionalOfferModalContent />
          </ModalPopup>

          <BottomSheet
            open={isBottomSheetOpen}
            onClose={handleCloseBottomSheet}
          >
            {memoizedBottomSheetContent}
          </BottomSheet>
        </Box>
      )}
    </Box>
  );
};

export default ApplicationDetails;
